import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Circle, Group, Layer, Line, Rect, Stage, Text } from "react-konva";
import { buildGeometryFigureResult, classifyStroke } from "../lib/geometryFigureEngine";
import ConfirmDialog from "./ConfirmDialog";
import { FigureRender } from "./FigureRender";

const RAW_STROKE_WIDTH = 1;
const PREVIEW_STROKE_WIDTH = 1.2;
const PREVIEW_MARKER_STROKE_WIDTH = 1.05;
const PREVIEW_POINT_RADIUS = 2.6;
const PREVIEW_CENTER_POINT_RADIUS = 3.5;
const PREVIEW_CENTER_POINT_STROKE = 1.15;
const PREVIEW_CIRCLE_STROKE_WIDTH = 1.15;
const PREVIEW_GUIDE_STROKE_WIDTH = 1;
const PREVIEW_LABEL_FONT_SIZE = 14;
const MIN_ZOOM_LEVEL = 1;
const MAX_ZOOM_LEVEL = 2.5;
const ZOOM_STEP = 0.25;
const DASHED_SEGMENT_DASH = Object.freeze([10, 6]);
const DOTTED_SEGMENT_DASH = Object.freeze([2.5, 7.5]);
const NON_SOLID_SEGMENT_STYLES = Object.freeze(["dashed", "dotted"]);

function resolveStageHeight(width) {
  if (width >= 960) return 420;
  if (width >= 720) return 380;
  if (width >= 520) return 340;
  return 320;
}

function getStageHeightBounds(width) {
  const min = resolveStageHeight(width);
  return {
    min,
    max: Math.max(560, min + 260)
  };
}

function normalizeStageHeight(width, height) {
  const bounds = getStageHeightBounds(width);
  return clamp(Math.round(Number(height) || bounds.min), bounds.min, bounds.max);
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function normalizeSegmentStrokeStyle(value, fallback = "solid") {
  const normalized = String(value || fallback || "solid").trim().toLowerCase();
  if (normalized === "dashed" || normalized === "dotted") return normalized;
  return "solid";
}

function resolveSegmentDashPattern(strokeStyle) {
  const normalized = normalizeSegmentStrokeStyle(strokeStyle, "solid");
  if (normalized === "dashed") return [...DASHED_SEGMENT_DASH];
  if (normalized === "dotted") return [...DOTTED_SEGMENT_DASH];
  return undefined;
}

function resolveSegmentStrokeStyleFromFigureLine(line) {
  const explicitStyle = normalizeSegmentStrokeStyle(line?.strokeStyle, "");
  if (explicitStyle !== "solid" || String(line?.strokeStyle || "").trim()) {
    return explicitStyle;
  }
  const dash = Array.isArray(line?.dash) ? line.dash : [];
  if (!dash.length) return "solid";
  return Number(dash[0]) <= 3 ? "dotted" : "dashed";
}

function distance(a, b) {
  const dx = Number((b?.x || 0) - (a?.x || 0));
  const dy = Number((b?.y || 0) - (a?.y || 0));
  return Math.hypot(dx, dy);
}

function remapPointStateByNearestPoint(currentState, previousPoints, nextPoints) {
  const entries = Object.entries(currentState || {});
  if (!entries.length) return currentState;

  const nextPointIds = new Set((Array.isArray(nextPoints) ? nextPoints : []).map((point) => point.id));
  const previousPointMap = new Map((Array.isArray(previousPoints) ? previousPoints : []).map((point) => [point.id, point]));
  const remapped = {};
  const usedPointIds = new Set();
  let changed = false;

  entries.forEach(([pointId, value]) => {
    if (nextPointIds.has(pointId)) {
      remapped[pointId] = value;
      usedPointIds.add(pointId);
      return;
    }

    const previousPoint = previousPointMap.get(pointId);
    if (!previousPoint) {
      changed = true;
      return;
    }

    let bestMatch = null;
    (Array.isArray(nextPoints) ? nextPoints : []).forEach((candidate) => {
      if (!candidate?.id || usedPointIds.has(candidate.id)) return;
      if (previousPoint.role && candidate.role && previousPoint.role !== candidate.role) return;
      const candidateDistance = distance(previousPoint, candidate);
      if (candidateDistance > 28) return;
      if (!bestMatch || candidateDistance < bestMatch.distance) {
        bestMatch = {
          id: candidate.id,
          distance: candidateDistance
        };
      }
    });

    if (bestMatch) {
      remapped[bestMatch.id] = value;
      usedPointIds.add(bestMatch.id);
      changed = true;
      return;
    }

    changed = true;
  });

  if (!changed && entries.length === Object.keys(remapped).length) {
    return currentState;
  }
  return remapped;
}

function pointToSegmentDistance(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const denominator = dx * dx + dy * dy;
  if (!denominator) return distance(point, start);
  const t = clamp(((point.x - start.x) * dx + (point.y - start.y) * dy) / denominator, 0, 1);
  return distance(point, {
    x: start.x + dx * t,
    y: start.y + dy * t
  });
}

function normalizeVector(vector) {
  const magnitude = Math.hypot(Number(vector?.x || 0), Number(vector?.y || 0));
  if (!magnitude) return null;
  return {
    x: Number(vector.x) / magnitude,
    y: Number(vector.y) / magnitude
  };
}

function isPointInsideRect(point, rect, padding = 0) {
  return (
    point.x >= rect.x - padding &&
    point.x <= rect.x + rect.width + padding &&
    point.y >= rect.y - padding &&
    point.y <= rect.y + rect.height + padding
  );
}

function doesSegmentHitRect(segment, rect) {
  const sampleCount = 14;
  for (let step = 0; step <= sampleCount; step += 1) {
    const t = step / sampleCount;
    const point = {
      x: segment.from.x + (segment.to.x - segment.from.x) * t,
      y: segment.from.y + (segment.to.y - segment.from.y) * t
    };
    if (isPointInsideRect(point, rect, 3)) return true;
  }
  return false;
}

function doesCircleHitRect(circle, rect) {
  const nearestX = clamp(circle.center.x, rect.x, rect.x + rect.width);
  const nearestY = clamp(circle.center.y, rect.y, rect.y + rect.height);
  const nearestDistance = distance(circle.center, { x: nearestX, y: nearestY });
  if (nearestDistance <= circle.radius + 5 && nearestDistance >= Math.max(0, circle.radius - 12)) {
    return true;
  }
  return isPointInsideRect(circle.center, rect, 2);
}

function rectsOverlap(rectA, rectB, padding = 0) {
  return (
    rectA.x < rectB.x + rectB.width + padding &&
    rectA.x + rectA.width > rectB.x - padding &&
    rectA.y < rectB.y + rectB.height + padding &&
    rectA.y + rectA.height > rectB.y - padding
  );
}

function pointToPolylineDistance(point, polylinePoints) {
  const points = Array.isArray(polylinePoints) ? polylinePoints : [];
  if (points.length === 1) return distance(point, points[0]);
  let best = Number.POSITIVE_INFINITY;
  for (let index = 0; index < points.length - 1; index += 1) {
    best = Math.min(best, pointToSegmentDistance(point, points[index], points[index + 1]));
  }
  return Number.isFinite(best) ? best : Number.POSITIVE_INFINITY;
}

function doesPolylineHitRect(polylinePoints, rect) {
  const points = Array.isArray(polylinePoints) ? polylinePoints : [];
  if (points.length < 2) return false;
  for (let index = 0; index < points.length - 1; index += 1) {
    if (doesSegmentHitRect({ from: points[index], to: points[index + 1] }, rect)) {
      return true;
    }
  }
  return false;
}

function buildRectFromCenter(center, width, height) {
  return {
    x: center.x - width / 2,
    y: center.y - height / 2,
    width,
    height
  };
}

function scoreLabelRect(rect, anchor, obstacles, placedRects, stageSize) {
  const safeStage = stageSize || { width: 0, height: 0 };
  const rectCenter = {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2
  };
  let score = distance(rectCenter, anchor) * 0.7;

  if (
    rect.x < 2 ||
    rect.y < 2 ||
    rect.x + rect.width > safeStage.width - 2 ||
    rect.y + rect.height > safeStage.height - 2
  ) {
    score += 1600;
  }

  (Array.isArray(obstacles?.points) ? obstacles.points : []).forEach((point) => {
    if (isPointInsideRect(point, rect, 6)) score += 620;
    else if (distance(rectCenter, point) < 18) score += 110;
  });

  (Array.isArray(placedRects) ? placedRects : []).forEach((placed) => {
    if (rectsOverlap(rect, placed, 3)) score += 1500;
    else {
      const placedCenter = {
        x: placed.x + placed.width / 2,
        y: placed.y + placed.height / 2
      };
      if (distance(rectCenter, placedCenter) < 20) score += 140;
    }
  });

  (Array.isArray(obstacles?.segments) ? obstacles.segments : []).forEach((segment) => {
    if (doesSegmentHitRect(segment, rect)) score += 720;
    else if (pointToSegmentDistance(rectCenter, segment.from, segment.to) < 15) score += 120;
  });

  (Array.isArray(obstacles?.polylines) ? obstacles.polylines : []).forEach((polyline) => {
    if (doesPolylineHitRect(polyline, rect)) score += 640;
    else if (pointToPolylineDistance(rectCenter, polyline) < 14) score += 120;
  });

  (Array.isArray(obstacles?.circles) ? obstacles.circles : []).forEach((circle) => {
    if (doesCircleHitRect(circle, rect)) score += 980;
    else if (distance(rectCenter, circle.center) < circle.radius + 14) score += 240;
  });

  return score;
}

function chooseBestRectPlacement(anchor, width, height, offsets, obstacles, placedRects, stageSize) {
  const candidateOffsets = Array.isArray(offsets) && offsets.length
    ? offsets
    : [
        { dx: 0, dy: 0 },
        { dx: 0, dy: -26 },
        { dx: 0, dy: 26 },
        { dx: 24, dy: 0 },
        { dx: -24, dy: 0 },
        { dx: 18, dy: -18 },
        { dx: -18, dy: -18 },
        { dx: 18, dy: 18 },
        { dx: -18, dy: 18 },
        { dx: 34, dy: -10 },
        { dx: -34, dy: -10 }
      ];

  let best = null;
  candidateOffsets.forEach((offset) => {
    const rect = buildRectFromCenter(
      {
        x: anchor.x + Number(offset?.dx || 0),
        y: anchor.y + Number(offset?.dy || 0)
      },
      width,
      height
    );
    const score = scoreLabelRect(rect, anchor, obstacles, placedRects, stageSize);
    if (!best || score < best.score) {
      best = {
        rect,
        score
      };
    }
  });

  const rawRect = best?.rect || buildRectFromCenter(anchor, width, height);
  const maxWidth = Math.max(width, Number(stageSize?.width || 0) || width);
  const maxHeight = Math.max(height, Number(stageSize?.height || 0) || height);
  return {
    ...rawRect,
    x: clamp(rawRect.x, 2, Math.max(2, maxWidth - rawRect.width - 2)),
    y: clamp(rawRect.y, 2, Math.max(2, maxHeight - rawRect.height - 2))
  };
}

function computeLabelLayouts(points, segments, circles, polylines, stageSize) {
  const layouts = {};
  const placedRects = [];
  const orderedPoints = [...(Array.isArray(points) ? points : [])].sort((a, b) => {
    if (Math.abs(a.y - b.y) > 18) return a.y - b.y;
    return a.x - b.x;
  });
  const obstacles = {
    points,
    segments,
    circles,
    polylines
  };

  orderedPoints.forEach((point) => {
    const text = String(point?.label || "");
    if (!text) return;
    const width = Math.max(18, text.length * 10 + 10);
    const height = 18;
    const chosenRect = chooseBestRectPlacement(
      point,
      width,
      height,
      [
        { dx: width / 2 + 22, dy: -height / 2 - 10 },
        { dx: width / 2 + 22, dy: height / 2 + 10 },
        { dx: -(width / 2 + 22), dy: -height / 2 - 10 },
        { dx: -(width / 2 + 22), dy: height / 2 + 10 },
        { dx: width / 2 + 10, dy: -height / 2 - 6 },
        { dx: width / 2 + 10, dy: height / 2 + 6 },
        { dx: -(width / 2 + 10), dy: -height / 2 - 6 },
        { dx: -(width / 2 + 10), dy: height / 2 + 6 },
        { dx: 0, dy: -(height / 2 + 14) },
        { dx: 0, dy: height / 2 + 14 },
        { dx: width / 2 + 14, dy: 0 },
        { dx: -(width / 2 + 14), dy: 0 },
        { dx: width / 2 + 30, dy: -4 },
        { dx: -(width / 2 + 30), dy: -4 },
        { dx: 0, dy: -(height / 2 + 24) },
        { dx: 0, dy: height / 2 + 24 }
      ],
      obstacles,
      placedRects,
      stageSize
    );
    const chosen = {
      x: chosenRect.x,
      y: chosenRect.y,
      width: chosenRect.width,
      height: chosenRect.height
    };
    layouts[point.id] = chosen;
    placedRects.push({
      x: chosen.x,
      y: chosen.y,
      width: chosen.width,
      height: chosen.height
    });
  });

  return layouts;
}

function computeOverlayLayouts(items, obstacles, stageSize, initialRects = []) {
  const layouts = {};
  const placedRects = [...(Array.isArray(initialRects) ? initialRects : [])];
  const orderedItems = [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    if (Math.abs((a.anchor?.y || 0) - (b.anchor?.y || 0)) > 18) {
      return (a.anchor?.y || 0) - (b.anchor?.y || 0);
    }
    return (a.anchor?.x || 0) - (b.anchor?.x || 0);
  });

  orderedItems.forEach((item) => {
    const width = Number(item?.width || 0) || 0;
    const height = Number(item?.height || 0) || 0;
    const anchor = item?.anchor || { x: 0, y: 0 };
    const rect = chooseBestRectPlacement(
      anchor,
      width,
      height,
      item?.offsets,
      obstacles,
      placedRects,
      stageSize
    );
    layouts[item.id] = rect;
    placedRects.push(rect);
  });

  return layouts;
}

function buildCanvasLabelChip(candidate, targetKind) {
  const entityLabel = String(candidate?.label || "").trim();
  const valueText = String(candidate?.text || "").trim();
  const text = valueText ? `${entityLabel}: ${valueText}` : entityLabel;
  const width = Math.max(targetKind === "angle" ? 38 : 32, text.length * 7.4 + 14);
  const height = 22;
  return {
    text,
    width,
    height,
    x: Number(candidate?.x || 0) - width / 2,
    y: Number(candidate?.y || 0) - height / 2
  };
}

function computeLeaderPoints(anchor, rect, threshold = 12) {
  if (!anchor || !rect) return null;
  const nearest = {
    x: clamp(anchor.x, rect.x + 2, rect.x + rect.width - 2),
    y: clamp(anchor.y, rect.y + 2, rect.y + rect.height - 2)
  };
  if (distance(anchor, nearest) < threshold) return null;
  return [anchor.x, anchor.y, nearest.x, nearest.y];
}

function toFigureSpecPos(point, stageSize) {
  const width = Math.max(1, Number(stageSize?.width || 0));
  const height = Math.max(1, Number(stageSize?.height || 0));
  return [
    Number((((Number(point?.x) || 0) / width) * 100).toFixed(3)),
    Number((((Number(point?.y) || 0) / height) * 100).toFixed(3))
  ];
}

function unwrapTemplateToken(value) {
  return String(value || "")
    .trim()
    .replace(/^\{\{\s*/, "")
    .replace(/\s*\}\}$/, "");
}

function cloneLineStroke(line) {
  if (!line || typeof line !== "object") return null;
  return {
    ...line,
    points: Array.isArray(line.points) ? [...line.points] : []
  };
}

function normalizePointPosition(pos) {
  if (pos && typeof pos === "object" && !Array.isArray(pos)) {
    const x = Number(pos.x);
    const y = Number(pos.y);
    if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
  }
  if (!Array.isArray(pos) || pos.length < 2) return null;
  const x = Number(pos[0]);
  const y = Number(pos[1]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

function denormalizeFigurePoint(pos, stageSize) {
  const normalized = normalizePointPosition(pos);
  if (!normalized) return null;
  return {
    x: (normalized.x / 100) * Math.max(1, Number(stageSize?.width || 1)),
    y: (normalized.y / 100) * Math.max(1, Number(stageSize?.height || 1))
  };
}

function denormalizeFigureRadius(radius, stageSize) {
  const value = Number(radius);
  if (!Number.isFinite(value)) return null;
  const base = Math.min(
    Math.max(1, Number(stageSize?.width || 1)),
    Math.max(1, Number(stageSize?.height || 1))
  );
  return (value / 100) * base;
}

function buildArcStrokePoints(center, radius, startAngle, endAngle, sampleCount = 36) {
  const cx = Number(center?.x);
  const cy = Number(center?.y);
  const r = Number(radius);
  let start = Number(startAngle);
  let end = Number(endAngle);
  if (!Number.isFinite(cx) || !Number.isFinite(cy) || !Number.isFinite(r) || r <= 0) return [];
  if (!Number.isFinite(start) || !Number.isFinite(end)) return [];
  while (start < 0) start += Math.PI * 2;
  while (end < 0) end += Math.PI * 2;
  while (start >= Math.PI * 2) start -= Math.PI * 2;
  while (end >= Math.PI * 2) end -= Math.PI * 2;
  let sweep = end - start;
  if (Math.abs(sweep) < 0.0001) sweep = Math.PI * 2;
  else if (sweep <= 0) sweep += Math.PI * 2;
  const steps = Math.max(12, sampleCount);
  const points = [];
  for (let index = 0; index <= steps; index += 1) {
    const t = index / steps;
    const angle = start + sweep * t;
    points.push(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
  }
  return points;
}

function buildCircleStrokePoints(center, radius, sampleCount = 48) {
  return buildArcStrokePoints(center, radius, 0, Math.PI * 2, sampleCount);
}

function normalizeLabelKind(value, fallback = "constant") {
  return String(value || fallback).trim().toLowerCase() === "variable" ? "variable" : "constant";
}

function inferAnnotationKind(annotation) {
  const explicitKind = String(annotation?.kind || "").trim().toLowerCase();
  if (explicitKind === "variable" || explicitKind === "constant") return explicitKind;
  const rawText = String(annotation?.text || "").trim();
  return /^\{\{[^}]+\}\}$/.test(rawText) ? "variable" : "constant";
}

function expandBindingKey(key, targetType) {
  const trimmed = String(key || "").trim();
  if (!trimmed) return [];
  const normalizedType = String(targetType || "").trim().toLowerCase();
  const keys = new Set([trimmed]);

  if (normalizedType === "arc") {
    const match = /^arc\s+([A-Za-z0-9]+)$/i.exec(trimmed);
    if (match) {
      const points = match[1];
      keys.add(`arc ${points.split("").reverse().join("")}`);
    }
    return [...keys];
  }

  if (/^[A-Za-z0-9]+$/.test(trimmed) && trimmed.length >= 2) {
    keys.add(trimmed.split("").reverse().join(""));
  }

  return [...keys];
}

function buildAnnotationBinding(annotation, targetType) {
  const text = unwrapTemplateToken(annotation?.displayText || annotation?.text);
  if (!text) return null;

  const rawKeys = [];
  (Array.isArray(annotation?.refs) ? annotation.refs : []).forEach((ref) => {
    const normalized = String(ref || "").trim();
    if (normalized) rawKeys.push(normalized);
  });

  const description = String(annotation?.description || "").trim();
  if (description) rawKeys.push(description);

  const matchKeys = [...new Set(rawKeys.flatMap((key) => expandBindingKey(key, targetType)))];
  if (!matchKeys.length) return null;

  return {
    text,
    kind: inferAnnotationKind(annotation),
    matchKeys
  };
}

function buildEditorStateFromFigureSpec(figureSpec, stageSize) {
  if (!figureSpec || typeof figureSpec !== "object") return null;
  const entities =
    figureSpec.entities && typeof figureSpec.entities === "object" ? figureSpec.entities : null;
  if (!entities) return null;

  const pointMap = Object.fromEntries(
    (Array.isArray(entities.points) ? entities.points : [])
      .map((point) => {
        const denormalized = denormalizeFigurePoint(point?.pos, stageSize);
        if (!point?.id || !denormalized) return null;
        return [point.id, denormalized];
      })
      .filter(Boolean)
  );

  const lines = [];
  (Array.isArray(entities.lines) ? entities.lines : []).forEach((line) => {
    const from = pointMap[line?.from];
    const to = pointMap[line?.to];
    if (!from || !to) return;
    const strokeStyle = resolveSegmentStrokeStyleFromFigureLine(line);
    lines.push({
      color: String(line?.color || "#0f172a").trim() || "#0f172a",
      points: [from.x, from.y, to.x, to.y],
      strokeStyle
    });
  });

  (Array.isArray(entities.shapes) ? entities.shapes : []).forEach((shape) => {
    if (shape?.type === "circle") {
      const center = pointMap[shape.center] || denormalizeFigurePoint([shape.cx, shape.cy], stageSize);
      const radius = denormalizeFigureRadius(shape.r ?? shape.radius, stageSize);
      if (!center || !Number.isFinite(radius) || radius <= 0) return;
      lines.push({
        color: "#0f172a",
        points: buildCircleStrokePoints(center, radius)
      });
      return;
    }
    if (shape?.type === "arc") {
      const center = denormalizeFigurePoint(shape.centerPos || shape.center, stageSize);
      const radius = denormalizeFigureRadius(shape.r ?? shape.radius, stageSize);
      if (!center || !Number.isFinite(radius) || radius <= 0) return;
      const points = buildArcStrokePoints(center, radius, shape.startAngle, shape.endAngle);
      if (points.length < 4) return;
      lines.push({
        color: "#0f172a",
        points
      });
      return;
    }
    if (shape?.type === "polyline") {
      const points = (Array.isArray(shape.points) ? shape.points : [])
        .map((point) => denormalizeFigurePoint(point, stageSize))
        .filter(Boolean)
        .flatMap((point) => [point.x, point.y]);
      if (points.length < 4) return;
      lines.push({
        color: "#0f172a",
        points
      });
    }
  });

  const bindings = {
    segments: [],
    angles: [],
    arcs: []
  };
  (Array.isArray(entities.annotations) ? entities.annotations : []).forEach((annotation) => {
    const targetType = String(annotation?.targetType || "").trim().toLowerCase();
    const binding = buildAnnotationBinding(annotation, targetType);
    if (!binding) return;
    if (targetType === "segment") bindings.segments.push(binding);
    else if (targetType === "angle") bindings.angles.push(binding);
    else if (targetType === "arc") bindings.arcs.push(binding);
  });

  return {
    snapshot: createEditorStateSnapshot({
      lines,
      labelOverrides: {},
      pointLabelKinds: {},
      hiddenPointLabels: {},
      segmentStyleOverrides: {},
      segmentLabelOverrides: {},
      angleLabelOverrides: {},
      arcLabelOverrides: {},
      circleOverrides: {},
      guidedCircles: [],
      arcOverrides: {},
      familyOverrides: {},
      selectedCircleSuggestionId: null,
      stageSize
    }),
    bindings
  };
}

function scalePointArray(points, sourceMeta, targetMeta) {
  const list = Array.isArray(points) ? points : [];
  const sourceWidth = Math.max(1, Number(sourceMeta?.width || targetMeta?.width || 1));
  const sourceHeight = Math.max(1, Number(sourceMeta?.height || targetMeta?.height || 1));
  const targetWidth = Math.max(1, Number(targetMeta?.width || sourceWidth || 1));
  const targetHeight = Math.max(1, Number(targetMeta?.height || sourceHeight || 1));
  const scaleX = targetWidth / sourceWidth;
  const scaleY = targetHeight / sourceHeight;
  const scaled = [];
  for (let index = 0; index < list.length; index += 2) {
    scaled.push(Number(list[index] || 0) * scaleX, Number(list[index + 1] || 0) * scaleY);
  }
  return scaled;
}

function scaleDistance(distanceValue, sourceMeta, targetMeta) {
  const sourceWidth = Math.max(1, Number(sourceMeta?.width || targetMeta?.width || 1));
  const sourceHeight = Math.max(1, Number(sourceMeta?.height || targetMeta?.height || 1));
  const targetWidth = Math.max(1, Number(targetMeta?.width || sourceWidth || 1));
  const targetHeight = Math.max(1, Number(targetMeta?.height || sourceHeight || 1));
  const scaleX = targetWidth / sourceWidth;
  const scaleY = targetHeight / sourceHeight;
  return Number(distanceValue || 0) * ((scaleX + scaleY) / 2);
}

function cloneAndScaleCenterOverride(override, sourceMeta, targetMeta) {
  if (!override || typeof override !== "object") return null;
  const center = override.center && typeof override.center === "object" ? override.center : null;
  if (!center) return { ...override };
  const [x = 0, y = 0] = scalePointArray([center.x, center.y], sourceMeta, targetMeta);
  return {
    ...override,
    center: {
      x,
      y
    }
  };
}

function createEditorStateSnapshot({
  lines,
  labelOverrides,
  pointLabelKinds,
  hiddenPointLabels,
  segmentStyleOverrides,
  segmentLabelOverrides,
  angleLabelOverrides,
  arcLabelOverrides,
  circleOverrides,
  guidedCircles,
  arcOverrides,
  familyOverrides,
  selectedCircleSuggestionId,
  stageSize,
  acceptedFigureSpec,
  acceptedDataUrl
}) {
  return {
    lines: (Array.isArray(lines) ? lines : []).map(cloneLineStroke).filter(Boolean),
    labelOverrides: { ...(labelOverrides || {}) },
    pointLabelKinds: { ...(pointLabelKinds || {}) },
    hiddenPointLabels: { ...(hiddenPointLabels || {}) },
    segmentStyleOverrides: { ...(segmentStyleOverrides || {}) },
    segmentLabelOverrides: { ...(segmentLabelOverrides || {}) },
    angleLabelOverrides: { ...(angleLabelOverrides || {}) },
    arcLabelOverrides: { ...(arcLabelOverrides || {}) },
    circleOverrides: Object.fromEntries(
      Object.entries(circleOverrides || {}).map(([key, value]) => [key, cloneAndScaleCenterOverride(value)])
    ),
    arcOverrides: Object.fromEntries(
      Object.entries(arcOverrides || {}).map(([key, value]) => [key, cloneAndScaleCenterOverride(value)])
    ),
    guidedCircles: (Array.isArray(guidedCircles) ? guidedCircles : [])
      .map((circle) => {
        if (!circle || typeof circle !== "object") return null;
        const center =
          circle.center &&
          Number.isFinite(Number(circle.center.x)) &&
          Number.isFinite(Number(circle.center.y))
            ? { x: Number(circle.center.x), y: Number(circle.center.y) }
            : null;
        const radius = Number(circle.radius);
        if (!center || !Number.isFinite(radius) || radius <= 0) return null;
        return {
          id: String(circle.id || "").trim(),
          label: String(circle.label || "").trim(),
          center,
          radius
        };
      })
      .filter((circle) => circle?.id && circle?.center && circle.radius > 0),
    familyOverrides: { ...(familyOverrides || {}) },
    selectedCircleSuggestionId: selectedCircleSuggestionId || null,
    sourceMeta:
      stageSize && typeof stageSize === "object"
        ? {
            width: Number(stageSize.width) || 0,
            height: Number(stageSize.height) || 0
          }
        : {},
    acceptedFigureSpec:
      acceptedFigureSpec && typeof acceptedFigureSpec === "object"
        ? JSON.parse(JSON.stringify(acceptedFigureSpec))
        : null,
    acceptedDataUrl: String(acceptedDataUrl || "").trim()
  };
}

function restoreEditorStateSnapshot(snapshot, stageSize) {
  if (!snapshot || typeof snapshot !== "object") return null;
  const sourceMeta =
    snapshot.sourceMeta && typeof snapshot.sourceMeta === "object" ? snapshot.sourceMeta : stageSize || {};
  return {
    lines: (Array.isArray(snapshot.lines) ? snapshot.lines : [])
      .map((line) =>
        line && typeof line === "object"
          ? {
              ...line,
              points: scalePointArray(line.points, sourceMeta, stageSize)
            }
          : null
      )
      .filter(Boolean),
    labelOverrides: { ...(snapshot.labelOverrides || {}) },
    pointLabelKinds: { ...(snapshot.pointLabelKinds || {}) },
    hiddenPointLabels: { ...(snapshot.hiddenPointLabels || {}) },
    segmentStyleOverrides: { ...(snapshot.segmentStyleOverrides || {}) },
    segmentLabelOverrides: { ...(snapshot.segmentLabelOverrides || {}) },
    angleLabelOverrides: { ...(snapshot.angleLabelOverrides || {}) },
    arcLabelOverrides: { ...(snapshot.arcLabelOverrides || {}) },
    circleOverrides: Object.fromEntries(
      Object.entries(snapshot.circleOverrides || {}).map(([key, value]) => [
        key,
        cloneAndScaleCenterOverride(value, sourceMeta, stageSize)
      ])
    ),
    arcOverrides: Object.fromEntries(
      Object.entries(snapshot.arcOverrides || {}).map(([key, value]) => [
        key,
        cloneAndScaleCenterOverride(value, sourceMeta, stageSize)
      ])
    ),
    guidedCircles: (Array.isArray(snapshot.guidedCircles) ? snapshot.guidedCircles : [])
      .map((circle) => {
        if (!circle || typeof circle !== "object") return null;
        const center =
          circle.center &&
          Number.isFinite(Number(circle.center.x)) &&
          Number.isFinite(Number(circle.center.y))
            ? (() => {
                const [x = 0, y = 0] = scalePointArray(
                  [Number(circle.center.x), Number(circle.center.y)],
                  sourceMeta,
                  stageSize
                );
                return { x, y };
              })()
            : null;
        const radius = scaleDistance(Number(circle.radius), sourceMeta, stageSize);
        if (!center || !Number.isFinite(radius) || radius <= 0) return null;
        return {
          id: String(circle.id || "").trim(),
          label: String(circle.label || "").trim(),
          center,
          radius
        };
      })
      .filter((circle) => circle?.id && circle?.center && circle.radius > 0),
    familyOverrides: { ...(snapshot.familyOverrides || {}) },
    selectedCircleSuggestionId: snapshot.selectedCircleSuggestionId || null,
    acceptedFigureSpec:
      snapshot.acceptedFigureSpec && typeof snapshot.acceptedFigureSpec === "object"
        ? JSON.parse(JSON.stringify(snapshot.acceptedFigureSpec))
        : null,
    acceptedDataUrl: String(snapshot.acceptedDataUrl || "").trim()
  };
}

const EMPTY_LIST = Object.freeze([]);
const EMPTY_PREVIEW = Object.freeze({});
const EMPTY_SUMMARY = Object.freeze({});

function FigureAssistPanel({
  title,
  helperText,
  resetKey,
  strokeColor = "#0f172a",
  suggestedPoints = [],
  suggestedLabels = [],
  enableValueLabels = false,
  initialEditorState = null,
  initialFigureSpec = null,
  initialAcceptedDataUrl = "",
  onPreviewChange,
  onAccept,
  onReject,
  acceptedStatus = null,
  showAcceptRejectControls = true
}) {
  const [lines, setLines] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [labelOverrides, setLabelOverrides] = useState({});
  const [pointLabelKinds, setPointLabelKinds] = useState({});
  const [hiddenPointLabels, setHiddenPointLabels] = useState({});
  const [segmentStyleOverrides, setSegmentStyleOverrides] = useState({});
  const [segmentLabelOverrides, setSegmentLabelOverrides] = useState({});
  const [angleLabelOverrides, setAngleLabelOverrides] = useState({});
  const [arcLabelOverrides, setArcLabelOverrides] = useState({});
  const [circleOverrides, setCircleOverrides] = useState({});
  const [guidedCircles, setGuidedCircles] = useState([]);
  const [draggableFreshCircleIds, setDraggableFreshCircleIds] = useState([]);
  const [arcOverrides, setArcOverrides] = useState({});
  const [familyOverrides, setFamilyOverrides] = useState({});
  const [selectedCircleSuggestionId, setSelectedCircleSuggestionId] = useState(null);
  const [acceptedSnapshot, setAcceptedSnapshot] = useState(null);
  const [loadedAcceptedFigureSpec, setLoadedAcceptedFigureSpec] = useState(null);
  const [loadedAcceptedDataUrl, setLoadedAcceptedDataUrl] = useState("");
  const [showLoadedAcceptedPreview, setShowLoadedAcceptedPreview] = useState(false);
  const [pendingFigureSpecBindings, setPendingFigureSpecBindings] = useState(null);
  const [labelEditor, setLabelEditor] = useState(null);
  const [labelOverlayMode, setLabelOverlayMode] = useState("none");
  const [panelTab, setPanelTab] = useState("drawing");
  const [isDrawing, setIsDrawing] = useState(false);
  const [showStrokeMenu, setShowStrokeMenu] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [drawStrokeColor, setDrawStrokeColor] = useState(strokeColor);
  const [drawStrokeStyle, setDrawStrokeStyle] = useState("solid");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusText, setStatusText] = useState("");
  const [accepted, setAccepted] = useState(acceptedStatus === "accepted");
  const [stageSize, setStageSize] = useState({ width: 520, height: resolveStageHeight(520) });
  const [manualStageHeight, setManualStageHeight] = useState(null);
  const [isStageMeasured, setIsStageMeasured] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const containerRef = useRef(null);
  const stageViewportRef = useRef(null);
  const stageRef = useRef(null);
  const strokeMenuRef = useRef(null);
  const labelEditorInputRef = useRef(null);
  const lastLabelEditorTargetRef = useRef("");
  const lastResetKeyRef = useRef(resetKey);
  const lastHydrationSignatureRef = useRef("");
  const resizeStateRef = useRef(null);
  const manualStageHeightRef = useRef(null);
  const prevLineCountRef = useRef(0);
  const prevPreviewCircleIdsRef = useRef([]);
  const suspendFreshCircleTrackingRef = useRef(false);
  const previousPreviewPointsRef = useRef([]);
  const lastStrokeFinishedAtRef = useRef(0);
  const lastMarkerInteractionRef = useRef({ markerId: "", at: 0 });

  const applyEditorStateSnapshot = useCallback(
    (snapshot, { markAccepted = true, status = "" } = {}) => {
      const restored = restoreEditorStateSnapshot(snapshot, stageSize);
      if (!restored) return false;
      suspendFreshCircleTrackingRef.current = true;
      setLines(restored.lines || []);
      setRedoStack([]);
      setLabelOverrides(restored.labelOverrides || {});
      setPointLabelKinds(restored.pointLabelKinds || {});
      setHiddenPointLabels(restored.hiddenPointLabels || {});
      setSegmentStyleOverrides(restored.segmentStyleOverrides || {});
      setSegmentLabelOverrides(restored.segmentLabelOverrides || {});
      setAngleLabelOverrides(restored.angleLabelOverrides || {});
      setArcLabelOverrides(restored.arcLabelOverrides || {});
      setCircleOverrides(restored.circleOverrides || {});
      setGuidedCircles(restored.guidedCircles || []);
      setDraggableFreshCircleIds([]);
      setArcOverrides(restored.arcOverrides || {});
      setFamilyOverrides(restored.familyOverrides || {});
      setSelectedCircleSuggestionId(restored.selectedCircleSuggestionId || null);
      setLoadedAcceptedFigureSpec(restored.acceptedFigureSpec || null);
      setLoadedAcceptedDataUrl(String(restored.acceptedDataUrl || "").trim());
      setShowLoadedAcceptedPreview(Boolean(restored.acceptedDataUrl || restored.acceptedFigureSpec) && markAccepted);
      setAcceptedSnapshot(createEditorStateSnapshot({ ...restored, stageSize }));
      setAccepted(markAccepted);
      setStatusText(status);
      setError("");
      return true;
    },
    [stageSize]
  );

  useEffect(() => {
    setAccepted(acceptedStatus === "accepted");
  }, [acceptedStatus]);

  useEffect(() => {
    setDrawStrokeColor(strokeColor);
  }, [strokeColor]);

  useEffect(() => {
    if (!showStrokeMenu) return undefined;
    function handlePointerDownOutside(event) {
      if (!strokeMenuRef.current) return;
      if (strokeMenuRef.current.contains(event.target)) return;
      setShowStrokeMenu(false);
    }
    document.addEventListener("pointerdown", handlePointerDownOutside);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDownOutside);
    };
  }, [showStrokeMenu]);

  useEffect(() => {
    if (lastResetKeyRef.current === resetKey) return;
    lastResetKeyRef.current = resetKey;
    lastHydrationSignatureRef.current = "";
    suspendFreshCircleTrackingRef.current = true;
    setLines([]);
    setRedoStack([]);
    setLabelOverrides({});
    setPointLabelKinds({});
    setHiddenPointLabels({});
    setSegmentStyleOverrides({});
    setSegmentLabelOverrides({});
    setAngleLabelOverrides({});
    setArcLabelOverrides({});
    setCircleOverrides({});
    setGuidedCircles([]);
    setDraggableFreshCircleIds([]);
    setArcOverrides({});
    setFamilyOverrides({});
    setSelectedCircleSuggestionId(null);
    setLabelOverlayMode("none");
    setShowStrokeMenu(false);
    setDrawStrokeColor(strokeColor);
    setDrawStrokeStyle("solid");
    setPanelTab("drawing");
    setAcceptedSnapshot(null);
    setLoadedAcceptedFigureSpec(null);
    setLoadedAcceptedDataUrl("");
    setShowLoadedAcceptedPreview(false);
    setPendingFigureSpecBindings(null);
    setLabelEditor(null);
    setError("");
    setStatusText("");
    setAccepted(acceptedStatus === "accepted");
    setIsStageMeasured(false);
    setZoomLevel(1);
  }, [acceptedStatus, resetKey, strokeColor]);

  useEffect(() => {
    if (!isStageMeasured) return;
    if (!initialEditorState || typeof initialEditorState !== "object") return;
    const signature = JSON.stringify({
      resetKey,
      initialEditorState
    });
    if (lastHydrationSignatureRef.current === signature) return;
    if (applyEditorStateSnapshot(initialEditorState, { markAccepted: true, status: "Saved figure loaded." })) {
      const acceptedSpec =
        initialEditorState?.acceptedFigureSpec && typeof initialEditorState.acceptedFigureSpec === "object"
          ? initialEditorState.acceptedFigureSpec
          : initialFigureSpec && typeof initialFigureSpec === "object"
            ? initialFigureSpec
            : null;
      const acceptedDataUrl = String(initialEditorState?.acceptedDataUrl || initialAcceptedDataUrl || "").trim();
      setLoadedAcceptedFigureSpec(acceptedSpec);
      setLoadedAcceptedDataUrl(acceptedDataUrl);
      setShowLoadedAcceptedPreview(Boolean(acceptedSpec || acceptedDataUrl));
      setPendingFigureSpecBindings(null);
      lastHydrationSignatureRef.current = signature;
    }
  }, [
    applyEditorStateSnapshot,
    initialAcceptedDataUrl,
    initialEditorState,
    initialFigureSpec,
    isStageMeasured,
    resetKey,
    stageSize.height,
    stageSize.width
  ]);

  useEffect(() => {
    if (!isStageMeasured) return;
    if (initialEditorState || !initialFigureSpec || typeof initialFigureSpec !== "object") return;
    if (lines.length || acceptedSnapshot) return;
    const fallback = buildEditorStateFromFigureSpec(initialFigureSpec, stageSize);
    if (!fallback?.snapshot) return;
    const signature = JSON.stringify({
      resetKey,
      fallback: "figureSpec"
    });
    if (lastHydrationSignatureRef.current === signature) return;
    if (
      applyEditorStateSnapshot(fallback.snapshot, {
        markAccepted: true,
        status: "Saved figure loaded from figure spec."
      })
    ) {
      setLoadedAcceptedFigureSpec(initialFigureSpec);
      setLoadedAcceptedDataUrl(String(initialAcceptedDataUrl || "").trim());
      setShowLoadedAcceptedPreview(true);
      setPendingFigureSpecBindings(fallback.bindings || null);
      lastHydrationSignatureRef.current = signature;
    }
  }, [
    acceptedSnapshot,
    applyEditorStateSnapshot,
    initialAcceptedDataUrl,
    initialEditorState,
    initialFigureSpec,
    lines.length,
    resetKey,
    isStageMeasured,
    stageSize
  ]);

  useEffect(() => {
    manualStageHeightRef.current = manualStageHeight;
  }, [manualStageHeight]);

  useEffect(() => {
    function updateStageSize() {
      if (!containerRef.current) return;
      const width = Math.max(320, Math.floor(containerRef.current.clientWidth));
      const height =
        manualStageHeightRef.current == null
          ? resolveStageHeight(width)
          : normalizeStageHeight(width, manualStageHeightRef.current);
      setStageSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
      setIsStageMeasured(true);
    }

    updateStageSize();
    window.addEventListener("resize", updateStageSize);
    let observer = null;
    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      observer = new ResizeObserver(() => updateStageSize());
      observer.observe(containerRef.current);
    }
    return () => {
      window.removeEventListener("resize", updateStageSize);
      if (observer) observer.disconnect();
    };
  }, []);

  const figureBuildOptions = useMemo(
    () => ({
      suggestedPoints,
      suggestedLabels,
      pointLabelKinds,
      hiddenPointLabels,
      segmentStyleOverrides,
      segmentLabelOverrides,
      angleLabelOverrides,
      arcLabelOverrides,
      circleOverrides,
      guidedCircles,
      arcOverrides,
      familyOverrides
    }),
    [
      arcLabelOverrides,
      arcOverrides,
      angleLabelOverrides,
      circleOverrides,
      familyOverrides,
      guidedCircles,
      hiddenPointLabels,
      pointLabelKinds,
      segmentStyleOverrides,
      segmentLabelOverrides,
      suggestedLabels,
      suggestedPoints
    ]
  );
  const result = useMemo(
    () => buildGeometryFigureResult(lines, stageSize, labelOverrides, figureBuildOptions),
    [figureBuildOptions, labelOverrides, lines, stageSize]
  );
  const preview = result?.preview || EMPTY_PREVIEW;
  const summary = result?.summary || EMPTY_SUMMARY;
  const zoomedStageSize = useMemo(
    () => ({
      width: Math.max(1, Math.round(stageSize.width * zoomLevel)),
      height: Math.max(1, Math.round(stageSize.height * zoomLevel))
    }),
    [stageSize.height, stageSize.width, zoomLevel]
  );
  const previewPoints = Array.isArray(preview.points) ? preview.points : EMPTY_LIST;
  const previewSegments = Array.isArray(preview.segments) ? preview.segments : EMPTY_LIST;
  const activeSegmentStyleIds = useMemo(
    () =>
      [...new Set(previewSegments.map((segment) => String(segment?.supportId || segment?.id || "").trim()).filter(Boolean))].sort(),
    [previewSegments]
  );
  const previewArcs = Array.isArray(preview.arcs) ? preview.arcs : EMPTY_LIST;
  const previewCircles = Array.isArray(preview.circles) ? preview.circles : EMPTY_LIST;
  const previewRightAngleMarkers = Array.isArray(preview.rightAngleMarkers)
    ? preview.rightAngleMarkers
    : EMPTY_LIST;
  const previewLengthTickMarkers = Array.isArray(preview.lengthTickMarkers)
    ? preview.lengthTickMarkers
    : EMPTY_LIST;
  const previewParallelArrowMarkers = Array.isArray(preview.parallelArrowMarkers)
    ? preview.parallelArrowMarkers
    : EMPTY_LIST;
  const previewAngleArcMarkers = Array.isArray(preview.angleArcMarkers)
    ? preview.angleArcMarkers
    : EMPTY_LIST;
  const hasStyledSegments = previewSegments.some((segment) =>
    NON_SOLID_SEGMENT_STYLES.includes(normalizeSegmentStrokeStyle(segment?.strokeStyle, "solid"))
  );
  const activeMarkerFamilyIds = useMemo(() => {
    const ids = new Set();
    [previewLengthTickMarkers, previewParallelArrowMarkers, previewAngleArcMarkers].forEach((markers) => {
      (Array.isArray(markers) ? markers : []).forEach((marker) => {
        const familyId = String(marker?.familyId || "").trim();
        if (familyId) ids.add(familyId);
      });
    });
    return [...ids].sort();
  }, [previewAngleArcMarkers, previewLengthTickMarkers, previewParallelArrowMarkers]);
  const previewCircleSuggestions = Array.isArray(preview.circleSuggestions) ? preview.circleSuggestions : EMPTY_LIST;
  const previewAnnotations = Array.isArray(preview.annotations) ? preview.annotations : EMPTY_LIST;
  const previewSegmentLabelCandidates = Array.isArray(preview.segmentLabelCandidates)
    ? preview.segmentLabelCandidates
    : EMPTY_LIST;
  const previewAngleLabelCandidates = Array.isArray(preview.angleLabelCandidates)
    ? preview.angleLabelCandidates
    : EMPTY_LIST;
  const previewArcLabelCandidates = Array.isArray(preview.arcLabelCandidates)
    ? preview.arcLabelCandidates
    : EMPTY_LIST;
  const previewPointSignature = useMemo(
    () =>
      previewPoints
        .map((point) => `${point.id}:${Number(point.x || 0).toFixed(1)}:${Number(point.y || 0).toFixed(1)}`)
        .join("|"),
    [previewPoints]
  );
  const activeRawLine = isDrawing && lines.length ? lines[lines.length - 1] : null;
  const entitySummary = Array.isArray(summary.entitySummary) ? summary.entitySummary : EMPTY_LIST;
  const relationSummary = Array.isArray(summary.relationSummary) ? summary.relationSummary : EMPTY_LIST;
  const givenSummary = Array.isArray(summary.givenSummary) ? summary.givenSummary : EMPTY_LIST;
  const dependencySummary = Array.isArray(summary.dependencySummary) ? summary.dependencySummary : EMPTY_LIST;
  const warnings = Array.isArray(result?.warnings) ? result.warnings : EMPTY_LIST;

  useEffect(() => {
    const activeSegmentSet = new Set(activeSegmentStyleIds);
    setSegmentStyleOverrides((prev) => {
      const entries = Object.entries(prev || {});
      if (!entries.length) return prev;
      const nextEntries = entries.filter(([segmentId]) => activeSegmentSet.has(segmentId));
      if (nextEntries.length === entries.length) return prev;
      return Object.fromEntries(nextEntries);
    });
  }, [activeSegmentStyleIds]);

  useEffect(() => {
    const activeFamilySet = new Set(activeMarkerFamilyIds);
    setFamilyOverrides((prev) => {
      const entries = Object.entries(prev || {});
      if (!entries.length) return prev;
      const nextEntries = entries.filter(([familyId]) => activeFamilySet.has(familyId));
      if (nextEntries.length === entries.length) return prev;
      return Object.fromEntries(nextEntries);
    });
  }, [activeMarkerFamilyIds]);

  useEffect(() => {
    const previousPreviewPoints = previousPreviewPointsRef.current;
    if (!previousPreviewPoints.length || !previewPoints.length) {
      previousPreviewPointsRef.current = previewPoints;
      return;
    }

    setLabelOverrides((prev) => remapPointStateByNearestPoint(prev, previousPreviewPoints, previewPoints));
    setPointLabelKinds((prev) => remapPointStateByNearestPoint(prev, previousPreviewPoints, previewPoints));
    setHiddenPointLabels((prev) => remapPointStateByNearestPoint(prev, previousPreviewPoints, previewPoints));

    if (labelEditor?.targetKind === "point" && labelEditor.targetId && !previewPoints.some((point) => point.id === labelEditor.targetId)) {
      const remappedEditorPoint = remapPointStateByNearestPoint(
        { [labelEditor.targetId]: true },
        previousPreviewPoints,
        previewPoints
      );
      const nextPointId = Object.keys(remappedEditorPoint)[0];
      if (nextPointId) {
        setLabelEditor((prev) => (prev ? { ...prev, targetId: nextPointId } : prev));
      } else {
        setLabelEditor(null);
      }
    }

    previousPreviewPointsRef.current = previewPoints;
  }, [labelEditor, previewPointSignature, previewPoints]);
  const canReviewLabels =
    previewPoints.length > 0 ||
    previewSegmentLabelCandidates.length > 0 ||
    previewAngleLabelCandidates.length > 0 ||
    previewArcLabelCandidates.length > 0;
  const geometryPolylines = useMemo(
    () => [
      ...previewArcs.map((arc) => Array.isArray(arc.points) ? arc.points : []),
      ...previewRightAngleMarkers.map((marker) => Array.isArray(marker.points) ? marker.points : []),
      ...previewLengthTickMarkers.map((marker) => Array.isArray(marker.points) ? marker.points : []),
      ...previewParallelArrowMarkers.map((marker) => Array.isArray(marker.points) ? marker.points : []),
      ...previewAngleArcMarkers.map((marker) => Array.isArray(marker.points) ? marker.points : [])
    ].filter((points) => points.length >= 2),
    [previewAngleArcMarkers, previewArcs, previewLengthTickMarkers, previewParallelArrowMarkers, previewRightAngleMarkers]
  );
  const labelLayouts = useMemo(
    () => computeLabelLayouts(previewPoints, previewSegments, previewCircles, geometryPolylines, stageSize),
    [geometryPolylines, previewCircles, previewPoints, previewSegments, stageSize]
  );
  const pointLabelRects = useMemo(
    () => Object.values(labelLayouts).map((layout) => ({
      x: layout.x,
      y: layout.y,
      width: layout.width,
      height: layout.height
    })),
    [labelLayouts]
  );
  const rawVisibleSegmentLabelCandidates = useMemo(
    () =>
      enableValueLabels && labelOverlayMode === "segment"
        ? previewSegmentLabelCandidates.filter((candidate) => !String(candidate?.text || "").trim())
        : [],
    [enableValueLabels, labelOverlayMode, previewSegmentLabelCandidates]
  );
  const rawVisibleAngleLabelCandidates = useMemo(
    () =>
      enableValueLabels && labelOverlayMode === "angle"
        ? previewAngleLabelCandidates.filter((candidate) => !String(candidate?.text || "").trim())
        : [],
    [enableValueLabels, labelOverlayMode, previewAngleLabelCandidates]
  );
  const rawVisibleArcLabelCandidates = useMemo(
    () =>
      enableValueLabels && labelOverlayMode === "arc"
        ? previewArcLabelCandidates.filter((candidate) => !String(candidate?.text || "").trim())
        : [],
    [enableValueLabels, labelOverlayMode, previewArcLabelCandidates]
  );
  const geometryObstacles = useMemo(
    () => ({
      points: previewPoints,
      segments: previewSegments,
      circles: previewCircles,
      polylines: geometryPolylines
    }),
    [geometryPolylines, previewCircles, previewPoints, previewSegments]
  );
  const candidateOverlayLayouts = useMemo(() => {
    const overlayItems = [
      ...rawVisibleSegmentLabelCandidates.map((candidate) => {
        const chip = buildCanvasLabelChip(candidate, "segment");
        return {
          id: `segment:${candidate.id}`,
          anchor: { x: Number(candidate.x || 0), y: Number(candidate.y || 0) },
          width: chip.width,
          height: chip.height
        };
      }),
      ...rawVisibleAngleLabelCandidates.map((candidate) => {
        const chip = buildCanvasLabelChip(candidate, "angle");
        return {
          id: `angle:${candidate.id}`,
          anchor: { x: Number(candidate.x || 0), y: Number(candidate.y || 0) },
          width: chip.width,
          height: chip.height
        };
      }),
      ...rawVisibleArcLabelCandidates.map((candidate) => {
        const chip = buildCanvasLabelChip(candidate, "angle");
        return {
          id: `arc:${candidate.id}`,
          anchor: { x: Number(candidate.x || 0), y: Number(candidate.y || 0) },
          width: chip.width,
          height: chip.height
        };
      }),
      ...previewAnnotations.map((annotation) => {
        const text = String(annotation?.text || "").trim();
        const width = Math.max(18, text.length * 7.1 + 6);
        const height = PREVIEW_LABEL_FONT_SIZE + 4;
        return {
          id: `annotation:${annotation.id}`,
          anchor: { x: Number(annotation.x || 0), y: Number(annotation.y || 0) },
          width,
          height
        };
      })
    ];
    return computeOverlayLayouts(overlayItems, geometryObstacles, stageSize, pointLabelRects);
  }, [
    geometryObstacles,
    pointLabelRects,
    previewAnnotations,
    rawVisibleAngleLabelCandidates,
    rawVisibleArcLabelCandidates,
    rawVisibleSegmentLabelCandidates,
    stageSize
  ]);
  const visibleSegmentLabelCandidates = useMemo(
    () =>
      rawVisibleSegmentLabelCandidates.map((candidate) => {
        const rect = candidateOverlayLayouts[`segment:${candidate.id}`];
        return rect
          ? {
              ...candidate,
              anchorX: Number(candidate.x || 0),
              anchorY: Number(candidate.y || 0),
              x: rect.x + rect.width / 2,
              y: rect.y + rect.height / 2
            }
          : {
              ...candidate,
              anchorX: Number(candidate.x || 0),
              anchorY: Number(candidate.y || 0)
            };
      }),
    [candidateOverlayLayouts, rawVisibleSegmentLabelCandidates]
  );
  const visibleAngleLabelCandidates = useMemo(
    () =>
      rawVisibleAngleLabelCandidates.map((candidate) => {
        const rect = candidateOverlayLayouts[`angle:${candidate.id}`];
        return rect
          ? {
              ...candidate,
              anchorX: Number(candidate.x || 0),
              anchorY: Number(candidate.y || 0),
              x: rect.x + rect.width / 2,
              y: rect.y + rect.height / 2
            }
          : {
              ...candidate,
              anchorX: Number(candidate.x || 0),
              anchorY: Number(candidate.y || 0)
            };
      }),
    [candidateOverlayLayouts, rawVisibleAngleLabelCandidates]
  );
  const visibleArcLabelCandidates = useMemo(
    () =>
      rawVisibleArcLabelCandidates.map((candidate) => {
        const rect = candidateOverlayLayouts[`arc:${candidate.id}`];
        return rect
          ? {
              ...candidate,
              anchorX: Number(candidate.x || 0),
              anchorY: Number(candidate.y || 0),
              x: rect.x + rect.width / 2,
              y: rect.y + rect.height / 2
            }
          : {
              ...candidate,
              anchorX: Number(candidate.x || 0),
              anchorY: Number(candidate.y || 0)
            };
      }),
    [candidateOverlayLayouts, rawVisibleArcLabelCandidates]
  );
  useEffect(() => {
    if (!pendingFigureSpecBindings) return;

    const nextSegmentLabelOverrides = {};
    const nextAngleLabelOverrides = {};
    const nextArcLabelOverrides = {};

    const matchBinding = (binding, candidates) =>
      (Array.isArray(candidates) ? candidates : []).find((candidate) =>
        binding.matchKeys.some((key) => key === String(candidate?.label || "").trim())
      );

    (Array.isArray(pendingFigureSpecBindings.segments) ? pendingFigureSpecBindings.segments : []).forEach((binding) => {
      const match = matchBinding(binding, previewSegmentLabelCandidates);
      if (!match?.id) return;
      nextSegmentLabelOverrides[match.id] = {
        text: binding.text,
        kind: normalizeLabelKind(binding.kind)
      };
    });

    (Array.isArray(pendingFigureSpecBindings.angles) ? pendingFigureSpecBindings.angles : []).forEach((binding) => {
      const match = matchBinding(binding, previewAngleLabelCandidates);
      if (!match?.id) return;
      nextAngleLabelOverrides[match.id] = {
        text: binding.text,
        kind: normalizeLabelKind(binding.kind)
      };
    });

    (Array.isArray(pendingFigureSpecBindings.arcs) ? pendingFigureSpecBindings.arcs : []).forEach((binding) => {
      const match = matchBinding(binding, previewArcLabelCandidates);
      if (!match?.id) return;
      nextArcLabelOverrides[match.id] = {
        text: binding.text,
        kind: normalizeLabelKind(binding.kind)
      };
    });

    const hasBindingMatch =
      Object.keys(nextSegmentLabelOverrides).length ||
      Object.keys(nextAngleLabelOverrides).length ||
      Object.keys(nextArcLabelOverrides).length;

    if (hasBindingMatch) {
      const mergedSegmentLabelOverrides = {
        ...(segmentLabelOverrides || {}),
        ...nextSegmentLabelOverrides
      };
      const mergedAngleLabelOverrides = {
        ...(angleLabelOverrides || {}),
        ...nextAngleLabelOverrides
      };
      const mergedArcLabelOverrides = {
        ...(arcLabelOverrides || {}),
        ...nextArcLabelOverrides
      };

      setSegmentLabelOverrides(mergedSegmentLabelOverrides);
      setAngleLabelOverrides(mergedAngleLabelOverrides);
      setArcLabelOverrides(mergedArcLabelOverrides);
      setAcceptedSnapshot(
        createEditorStateSnapshot({
          lines,
          labelOverrides,
          pointLabelKinds,
          hiddenPointLabels,
          segmentStyleOverrides,
          segmentLabelOverrides: mergedSegmentLabelOverrides,
          angleLabelOverrides: mergedAngleLabelOverrides,
          arcLabelOverrides: mergedArcLabelOverrides,
          circleOverrides,
          guidedCircles,
          arcOverrides,
          familyOverrides,
          selectedCircleSuggestionId,
          stageSize,
          acceptedFigureSpec: loadedAcceptedFigureSpec,
          acceptedDataUrl: loadedAcceptedDataUrl
        })
      );
    }

    setPendingFigureSpecBindings(null);
  }, [
    angleLabelOverrides,
    arcLabelOverrides,
    arcOverrides,
    circleOverrides,
    familyOverrides,
    labelOverrides,
    lines,
    loadedAcceptedDataUrl,
    loadedAcceptedFigureSpec,
    pendingFigureSpecBindings,
    pointLabelKinds,
    hiddenPointLabels,
    segmentStyleOverrides,
    previewAngleLabelCandidates,
    previewArcLabelCandidates,
    previewSegmentLabelCandidates,
    segmentLabelOverrides,
    selectedCircleSuggestionId,
    guidedCircles,
    stageSize
  ]);
  const canvasPreviewAnnotations = useMemo(
    () =>
      previewAnnotations.map((annotation) => {
        const rect = candidateOverlayLayouts[`annotation:${annotation.id}`];
        return rect
          ? {
              ...annotation,
              anchorX: Number(annotation.anchorX ?? annotation.x ?? 0),
              anchorY: Number(annotation.anchorY ?? annotation.y ?? 0),
              x: rect.x + 4,
              y: rect.y + PREVIEW_LABEL_FONT_SIZE
            }
          : {
              ...annotation,
              anchorX: Number(annotation.anchorX ?? annotation.x ?? 0),
              anchorY: Number(annotation.anchorY ?? annotation.y ?? 0)
            };
      }),
    [candidateOverlayLayouts, previewAnnotations]
  );
  const activeCircleSuggestion = useMemo(
    () =>
      previewCircleSuggestions.find((item) => item.id === selectedCircleSuggestionId) || null,
    [previewCircleSuggestions, selectedCircleSuggestionId]
  );
  const hasActiveSuggestedCircleInPreview = useMemo(
    () =>
      Boolean(
        activeCircleSuggestion &&
          previewCircles.some(
            (circle) =>
              distance(circle.center, activeCircleSuggestion.center) <= 6 &&
              Math.abs(circle.radius - activeCircleSuggestion.radius) <= 6
          )
      ),
    [activeCircleSuggestion, previewCircles]
  );
  const canUndo = !loading && lines.length > 0;
  const canRedo = !loading && redoStack.length > 0;
  const canClear =
    !loading &&
    Boolean(lines.length || acceptedSnapshot || loadedAcceptedFigureSpec || loadedAcceptedDataUrl || showLoadedAcceptedPreview);
  const circleByCenterNodeId = useMemo(() => {
    const entries = previewCircles
      .filter((circle) => circle?.centerNode?.id)
      .map((circle) => [circle.centerNode.id, circle]);
    return new Map(entries);
  }, [previewCircles]);

  useEffect(() => {
    const previewCircleIds = previewCircles.map((circle) => String(circle?.id || "").trim()).filter(Boolean);
    if (suspendFreshCircleTrackingRef.current) {
      prevLineCountRef.current = lines.length;
      prevPreviewCircleIdsRef.current = previewCircleIds;
      suspendFreshCircleTrackingRef.current = false;
      setDraggableFreshCircleIds([]);
      return;
    }

    const previousLineCount = prevLineCountRef.current;
    const previousCircleIds = new Set(prevPreviewCircleIdsRef.current);

    if (lines.length > previousLineCount) {
      const lastStroke = lines[lines.length - 1];
      const classifiedStroke = classifyStroke(lastStroke, lines.length - 1);
      if (classifiedStroke?.kind === "circle") {
        setDraggableFreshCircleIds(
          previewCircleIds.filter((circleId) => !previousCircleIds.has(circleId))
        );
      } else {
        setDraggableFreshCircleIds([]);
      }
    } else if (lines.length < previousLineCount) {
      setDraggableFreshCircleIds([]);
    }

    prevLineCountRef.current = lines.length;
    prevPreviewCircleIdsRef.current = previewCircleIds;
  }, [lines, previewCircles]);

  function toStagePoint(point) {
    return {
      x: Number(point?.x || 0) * zoomLevel,
      y: Number(point?.y || 0) * zoomLevel
    };
  }

  function getCanvasPointer(event) {
    const stage = event?.target?.getStage?.();
    const point = stage?.getPointerPosition?.();
    if (!point) return null;
    return {
      x: point.x / zoomLevel,
      y: point.y / zoomLevel
    };
  }

  function setNextZoomLevel(updater) {
    setZoomLevel((prev) => {
      const nextRaw = typeof updater === "function" ? updater(prev) : updater;
      const next = clamp(Number(nextRaw) || 1, MIN_ZOOM_LEVEL, MAX_ZOOM_LEVEL);
      return Math.round(next * 100) / 100;
    });
  }

  useEffect(() => {
    if (!labelEditor?.targetId || !labelEditor?.targetKind) return;
    const exists =
      (labelEditor.targetKind === "point" &&
        previewPoints.some((point) => point.id === labelEditor.targetId)) ||
      (labelEditor.targetKind === "segment" &&
        previewSegmentLabelCandidates.some((item) => item.id === labelEditor.targetId)) ||
      (labelEditor.targetKind === "angle" &&
        previewAngleLabelCandidates.some((item) => item.id === labelEditor.targetId)) ||
      (labelEditor.targetKind === "arc" &&
        previewArcLabelCandidates.some((item) => item.id === labelEditor.targetId));
    if (!exists) {
      setLabelEditor(null);
    }
  }, [labelEditor, previewAngleLabelCandidates, previewArcLabelCandidates, previewPoints, previewSegmentLabelCandidates]);

  useEffect(() => {
    if (!selectedCircleSuggestionId) return;
    if (!previewCircleSuggestions.some((item) => item.id === selectedCircleSuggestionId)) {
      setSelectedCircleSuggestionId(null);
    }
  }, [previewCircleSuggestions, selectedCircleSuggestionId]);

  useEffect(() => {
    if (typeof onPreviewChange !== "function") return;
    onPreviewChange({
      ...result,
      sourceLines: lines,
      sourceMeta: stageSize,
      status: accepted ? "accepted" : "draft"
    });
  }, [accepted, lines, onPreviewChange, result, stageSize]);

  useEffect(() => {
    if (!labelEditor?.targetKind || !labelEditor?.targetId) {
      lastLabelEditorTargetRef.current = "";
      return;
    }
    if (!labelEditorInputRef.current) return;
    const targetKey = `${labelEditor.targetKind}:${labelEditor.targetId}`;
    labelEditorInputRef.current.focus();
    if (lastLabelEditorTargetRef.current !== targetKey) {
      labelEditorInputRef.current.select();
      lastLabelEditorTargetRef.current = targetKey;
    }
  }, [labelEditor?.targetKind, labelEditor?.targetId]);

  function updatePointLabel(pointId, nextValue) {
    const normalizedLabel = String(nextValue || "")
      .trim()
      .replace(/\s+/g, "");
    const activePoint = previewPoints.find((point) => point.id === pointId);
    if (!activePoint) return;

    setLabelOverrides((prev) => {
      const next = { ...prev };
      if (!normalizedLabel) {
        delete next[pointId];
        return next;
      }

      const currentLabel = String(activePoint.label || "").trim();
      const duplicatePoint = previewPoints.find(
        (point) => point.id !== pointId && String(point.label || "").trim().toLowerCase() === normalizedLabel.toLowerCase()
      );

      if (duplicatePoint && currentLabel) {
        next[duplicatePoint.id] = currentLabel;
      }

      next[pointId] = normalizedLabel;
      return next;
    });
    setHiddenPointLabels((prev) => {
      if (!prev[pointId]) return prev;
      const next = { ...prev };
      delete next[pointId];
      return next;
    });
    setAccepted(false);
    setStatusText("");
  }

  function hidePointLabel(pointId) {
    if (!pointId) return;
    setHiddenPointLabels((prev) => ({
      ...prev,
      [pointId]: true
    }));
    setLabelOverrides((prev) => {
      if (!prev[pointId]) return prev;
      const next = { ...prev };
      delete next[pointId];
      return next;
    });
    setAccepted(false);
    setStatusText("");
  }

  function updatePointLabelKind(pointId, nextKind) {
    setPointLabelKinds((prev) => ({
      ...prev,
      [pointId]: String(nextKind || "constant").trim().toLowerCase() === "variable" ? "variable" : "constant"
    }));
    setHiddenPointLabels((prev) => {
      if (!prev[pointId]) return prev;
      const next = { ...prev };
      delete next[pointId];
      return next;
    });
    setAccepted(false);
    setStatusText("");
  }

  function updateEntityLabelOverride(targetKind, targetId, field, value) {
    const setter =
      targetKind === "segment"
        ? setSegmentLabelOverrides
        : targetKind === "arc"
        ? setArcLabelOverrides
        : setAngleLabelOverrides;
    setter((prev) => ({
      ...prev,
      [targetId]: {
        ...(prev[targetId] || {}),
        [field]:
          field === "kind"
            ? String(value || "constant").trim().toLowerCase() === "variable"
              ? "variable"
              : "constant"
            : value
      }
    }));
    setAccepted(false);
    setStatusText("");
  }

  function setCircleOverride(circleId, nextValue) {
    setCircleOverrides((prev) => ({
      ...prev,
      [circleId]: {
        ...(prev[circleId] || {}),
        ...nextValue
      }
    }));
    setAccepted(false);
    setStatusText("");
  }

  function setArcOverride(arcId, nextValue) {
    setArcOverrides((prev) => ({
      ...prev,
      [arcId]: {
        ...(prev[arcId] || {}),
        ...nextValue
      }
    }));
    setAccepted(false);
    setStatusText("");
  }

  function cycleMarkerFamilyCount(familyId, currentCount, familyKind) {
    if (!familyId) return;
    const safeCount = Math.max(1, Math.min(3, Math.round(Number(currentCount) || 1)));
    const nextCount = safeCount >= 3 ? 1 : safeCount + 1;
    const kindLabels = {
      arc: nextCount === 1 ? "arc" : "arcs",
      tick: nextCount === 1 ? "tick" : "ticks",
      arrow: nextCount === 1 ? "arrow" : "arrows"
    };
    const familyLabels = {
      arc: "Angle mark",
      tick: "Length mark",
      arrow: "Parallel mark"
    };
    setFamilyOverrides((prev) => ({
      ...prev,
      [familyId]: nextCount
    }));
    setAccepted(false);
    setStatusText(
      `${familyLabels[familyKind] || "Marker"} set to ${nextCount} ${kindLabels[familyKind] || "marks"}.`
    );
  }

  function snapCircleCandidate(nextCenter, nextRadius) {
    let center = nextCenter;
    let radius = Math.max(8, Number(nextRadius) || 8);

    if (activeCircleSuggestion) {
      if (distance(center, activeCircleSuggestion.center) <= 20) {
        center = {
          x: activeCircleSuggestion.center.x,
          y: activeCircleSuggestion.center.y
        };
      }
      if (Math.abs(radius - activeCircleSuggestion.radius) <= 12) {
        radius = activeCircleSuggestion.radius;
      }
      if (
        distance(center, activeCircleSuggestion.center) <= 20 &&
        Math.abs(radius - activeCircleSuggestion.radius) <= 14
      ) {
        center = {
          x: activeCircleSuggestion.center.x,
          y: activeCircleSuggestion.center.y
        };
        radius = activeCircleSuggestion.radius;
      }
    }

    const radiusCandidates = previewSegments
      .map((segment) => pointToSegmentDistance(center, segment.from, segment.to))
      .filter((value) => Number.isFinite(value) && value > 6);
    const nearestRadius = radiusCandidates.sort((a, b) => Math.abs(a - radius) - Math.abs(b - radius))[0];
    if (Number.isFinite(nearestRadius) && Math.abs(nearestRadius - radius) <= 8) {
      radius = nearestRadius;
    }

    return { center, radius };
  }

  function handleCircleCenterDrag(circle, event) {
    event.cancelBubble = true;
    const nextCenter = {
      x: event.target.x(),
      y: event.target.y()
    };
    const snapped = snapCircleCandidate(nextCenter, circle.radius);
    setCircleOverride(circle.id, snapped);
  }

  function handleCircleRadiusDrag(circle, event) {
    event.cancelBubble = true;
    const handlePoint = {
      x: event.target.x(),
      y: event.target.y()
    };
    const rawRadius = distance(circle.center, handlePoint);
    const snapped = snapCircleCandidate(circle.center, rawRadius);
    setCircleOverride(circle.id, snapped);
  }

  function snapArcCenterCandidate(arc, rawCenter) {
    const start = arc?.startPoint;
    const end = arc?.endPoint;
    if (!start || !end) return rawCenter;
    const chordVector = normalizeVector({
      x: end.x - start.x,
      y: end.y - start.y
    });
    if (!chordVector) return rawCenter;
    const chordMidpoint = {
      x: (start.x + end.x) / 2,
      y: (start.y + end.y) / 2
    };
    const perpendicular = {
      x: -chordVector.y,
      y: chordVector.x
    };
    let offset =
      (Number(rawCenter?.x || chordMidpoint.x) - chordMidpoint.x) * perpendicular.x +
      (Number(rawCenter?.y || chordMidpoint.y) - chordMidpoint.y) * perpendicular.y;
    if (Math.abs(offset) < 2.4) {
      const fallbackOffset =
        (Number(arc.center?.x || chordMidpoint.x) - chordMidpoint.x) * perpendicular.x +
        (Number(arc.center?.y || chordMidpoint.y) - chordMidpoint.y) * perpendicular.y;
      offset = fallbackOffset >= 0 ? 2.4 : -2.4;
    }
    const maxOffset = Math.max(18, distance(start, end) * 2.8);
    const clampedOffset = clamp(offset, -maxOffset, maxOffset);
    return {
      x: chordMidpoint.x + perpendicular.x * clampedOffset,
      y: chordMidpoint.y + perpendicular.y * clampedOffset
    };
  }

  function handleArcCenterDrag(arc, event) {
    event.cancelBubble = true;
    const rawCenter = {
      x: event.target.x(),
      y: event.target.y()
    };
    setArcOverride(arc.id, {
      center: snapArcCenterCandidate(arc, rawCenter)
    });
  }

  function applyCircleSuggestion() {
    if (!activeCircleSuggestion) return;
    const matchingCircle = previewCircles.find(
      (circle) =>
        distance(circle.center, activeCircleSuggestion.center) <= 8 &&
        Math.abs(circle.radius - activeCircleSuggestion.radius) <= 8
    );
    const circleId = matchingCircle?.id || String(activeCircleSuggestion.id || "").trim() || "guided_circle_1";
    if (!matchingCircle) {
      setGuidedCircles((prev) => {
        const withoutCurrent = (Array.isArray(prev) ? prev : []).filter((circle) => circle?.id !== circleId);
        return [
          ...withoutCurrent,
          {
            id: circleId,
            label: String(activeCircleSuggestion.label || "").trim() || null,
            center: {
              x: activeCircleSuggestion.center.x,
              y: activeCircleSuggestion.center.y
            },
            radius: activeCircleSuggestion.radius
          }
        ];
      });
    }
    setCircleOverride(circleId, {
      center: {
        x: activeCircleSuggestion.center.x,
        y: activeCircleSuggestion.center.y
      },
      radius: activeCircleSuggestion.radius
    });
    setDraggableFreshCircleIds([circleId]);
    setSelectedCircleSuggestionId(null);
    setAccepted(false);
    setStatusText(`Circle snapped to the selected ${String(activeCircleSuggestion.label || "guide").toLowerCase()}.`);
  }

  function openPointLabelEditor(point) {
    if (!point) return;
    const layout = labelLayouts[point.id];
    const stagePoint = toStagePoint({
      x: layout?.x ?? point.x + 12,
      y: layout?.y ?? point.y - 14
    });
    setLabelEditor({
      targetKind: "point",
      targetId: point.id,
      value: unwrapTemplateToken(point.label || point.semanticLabel || ""),
      kind: pointLabelKinds[point.id] || "constant",
      left: Math.max(12, Math.min(stagePoint.x, zoomedStageSize.width - 92)),
      top: Math.max(12, Math.min(stagePoint.y, zoomedStageSize.height - 46))
    });
  }

  function openEntityLabelEditor(targetKind, candidate) {
    if (!candidate?.id) return;
    const stagePoint = toStagePoint({
      x: Number(candidate.x || 0) + 10,
      y: Number(candidate.y || 0) - 12
    });
    setLabelEditor({
      targetKind,
      targetId: candidate.id,
      value: String(candidate.text || "").trim(),
      kind: String(candidate.kind || "constant").trim().toLowerCase() || "constant",
      left: Math.max(12, Math.min(stagePoint.x, zoomedStageSize.width - 148)),
      top: Math.max(12, Math.min(stagePoint.y, zoomedStageSize.height - 84))
    });
  }

  function commitLabelEditor() {
    if (!labelEditor?.targetId || !labelEditor?.targetKind) return;
    if (labelEditor.targetKind === "point") {
      updatePointLabel(labelEditor.targetId, labelEditor.value);
      if (enableValueLabels) {
        updatePointLabelKind(labelEditor.targetId, labelEditor.kind);
      }
    } else if (
      labelEditor.targetKind === "segment" ||
      labelEditor.targetKind === "angle" ||
      labelEditor.targetKind === "arc"
    ) {
      updateEntityLabelOverride(labelEditor.targetKind, labelEditor.targetId, "text", labelEditor.value);
      updateEntityLabelOverride(labelEditor.targetKind, labelEditor.targetId, "kind", labelEditor.kind);
    }
    setLabelEditor(null);
  }

  function clearLabelEditor() {
    if (!labelEditor?.targetId || !labelEditor?.targetKind) return;
    if (labelEditor.targetKind === "point") {
      hidePointLabel(labelEditor.targetId);
      setPointLabelKinds((prev) => {
        const next = { ...prev };
        delete next[labelEditor.targetId];
        return next;
      });
      setLabelEditor(null);
      return;
    }
    updateEntityLabelOverride(labelEditor.targetKind, labelEditor.targetId, "text", "");
    setLabelEditor(null);
  }

  function toggleLabelOverlayMode(nextMode) {
    setLabelOverlayMode((prev) => (prev === nextMode ? "none" : nextMode));
  }

  function handleResizePointerDown(event) {
    event.preventDefault();
    event.stopPropagation();
    resizeStateRef.current = {
      pointerId: Number.isFinite(Number(event.pointerId)) ? Number(event.pointerId) : null,
      startY: Number(event.clientY) || 0,
      startHeight: stageSize.height
    };
    if (event.currentTarget?.setPointerCapture && Number.isFinite(Number(event.pointerId))) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (err) {
        // Ignore pointer capture failures.
      }
    }
  }

  function handleResizePointerMove(event) {
    const resizeState = resizeStateRef.current;
    if (!resizeState) return;
    if (resizeState.pointerId !== null && Number(event.pointerId) !== resizeState.pointerId) return;
    event.preventDefault();
    const nextHeight = normalizeStageHeight(stageSize.width, resizeState.startHeight + (event.clientY - resizeState.startY));
    setManualStageHeight(nextHeight);
    setStageSize((prev) => (prev.height === nextHeight ? prev : { ...prev, height: nextHeight }));
  }

  function handleResizePointerEnd(event) {
    const resizeState = resizeStateRef.current;
    if (!resizeState) return;
    if (
      resizeState.pointerId !== null &&
      Number(event?.pointerId) !== resizeState.pointerId &&
      event?.type !== "lostpointercapture"
    ) {
      return;
    }
    if (event?.currentTarget?.releasePointerCapture && Number.isFinite(Number(event.pointerId))) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch (err) {
        // Ignore pointer capture failures.
      }
    }
    resizeStateRef.current = null;
  }

  function handlePointerDown(event) {
    if (
      typeof event?.target?.findAncestor === "function" &&
      event.target.findAncestor(".figure-interactive", true)
    ) {
      return;
    }
    if (labelEditor) {
      setLabelEditor(null);
    }
    if (showLoadedAcceptedPreview) {
      setShowLoadedAcceptedPreview(false);
    }
    event.evt.preventDefault();
    const point = getCanvasPointer(event);
    if (!point) return;
    const pointerType = String(event?.evt?.pointerType || "mouse").trim().toLowerCase() || "mouse";
    setShowStrokeMenu(false);
    setIsDrawing(true);
    setAccepted(false);
    setRedoStack([]);
    setStatusText("");
    setLines((prev) => [
      ...prev,
      { color: drawStrokeColor, points: [point.x, point.y], pointerType, strokeStyle: drawStrokeStyle }
    ]);
  }

  function handlePointerMove(event) {
    if (!isDrawing) return;
    if (event?.evt?.preventDefault) {
      event.evt.preventDefault();
    }
    const point = getCanvasPointer(event);
    if (!point) return;
    setLines((prev) => {
      const last = prev[prev.length - 1];
      if (!last) return prev;
      const updated = { ...last, points: [...last.points, point.x, point.y] };
      return [...prev.slice(0, -1), updated];
    });
  }

  function handlePointerUp(event) {
    if (event?.evt?.preventDefault) {
      event.evt.preventDefault();
    }
    if (isDrawing) {
      lastStrokeFinishedAtRef.current = Date.now();
    }
    setIsDrawing(false);
  }

  function runGuardedInteractiveAction(event, interactionId, action) {
    event.cancelBubble = true;
    const now = Date.now();
    if (now - lastStrokeFinishedAtRef.current <= 250) {
      return;
    }
    const lastInteraction = lastMarkerInteractionRef.current;
    if (lastInteraction.markerId === interactionId && now - lastInteraction.at <= 350) {
      return;
    }
    lastMarkerInteractionRef.current = { markerId: interactionId, at: now };
    action();
  }

  function handleMarkerFamilyInteraction(event, markerId, familyId, currentCount, familyKind) {
    runGuardedInteractiveAction(event, markerId, () => {
      cycleMarkerFamilyCount(familyId, currentCount, familyKind);
    });
  }

  function cycleSegmentStrokeStyle(segmentId, currentStyle) {
    if (!segmentId) return;
    const normalizedCurrent = normalizeSegmentStrokeStyle(currentStyle, "dashed");
    const nextStyle = normalizedCurrent === "dotted" ? "dashed" : "dotted";
    setSegmentStyleOverrides((prev) => ({
      ...prev,
      [segmentId]: nextStyle
    }));
    setAccepted(false);
    setStatusText(`Line style set to ${nextStyle}.`);
  }

  function handleSegmentStyleInteraction(event, segment) {
    const segmentId = String(segment?.supportId || segment?.id || "").trim();
    const currentStyle = normalizeSegmentStrokeStyle(segment?.strokeStyle, "solid");
    if (!segmentId || !NON_SOLID_SEGMENT_STYLES.includes(currentStyle)) return;
    runGuardedInteractiveAction(event, `segment-style:${segmentId}`, () => {
      cycleSegmentStrokeStyle(segmentId, currentStyle);
    });
  }

  function handleZoomIn() {
    setLabelEditor(null);
    setShowStrokeMenu(false);
    setNextZoomLevel((prev) => prev + ZOOM_STEP);
  }

  function handleZoomOut() {
    setLabelEditor(null);
    setShowStrokeMenu(false);
    setNextZoomLevel((prev) => prev - ZOOM_STEP);
  }

  function handleZoomReset() {
    setLabelEditor(null);
    setShowStrokeMenu(false);
    setNextZoomLevel(1);
    if (stageViewportRef.current) {
      stageViewportRef.current.scrollTo({ left: 0, top: 0 });
    }
  }

  function handleUndo() {
    setShowStrokeMenu(false);
    setShowLoadedAcceptedPreview(false);
    suspendFreshCircleTrackingRef.current = false;
    setLines((prev) => {
      if (!prev.length) return prev;
      const removed = prev[prev.length - 1];
      setRedoStack((redoPrev) => [removed, ...redoPrev]);
      return prev.slice(0, -1);
    });
    setAccepted(false);
    setStatusText("");
  }

  function handleRedo() {
    setShowStrokeMenu(false);
    setShowLoadedAcceptedPreview(false);
    suspendFreshCircleTrackingRef.current = false;
    setRedoStack((prev) => {
      if (!prev.length) return prev;
      const [first, ...rest] = prev;
      setLines((linePrev) => [...linePrev, first]);
      return rest;
    });
    setAccepted(false);
    setStatusText("");
  }

  function handleClear() {
    setShowStrokeMenu(false);
    suspendFreshCircleTrackingRef.current = true;
    setLines([]);
    setRedoStack([]);
    setLabelOverrides({});
    setPointLabelKinds({});
    setHiddenPointLabels({});
    setSegmentStyleOverrides({});
    setSegmentLabelOverrides({});
    setAngleLabelOverrides({});
    setArcLabelOverrides({});
    setCircleOverrides({});
    setGuidedCircles([]);
    setDraggableFreshCircleIds([]);
    setArcOverrides({});
    setFamilyOverrides({});
    setSelectedCircleSuggestionId(null);
    setLabelOverlayMode("none");
    setPanelTab("drawing");
    setAcceptedSnapshot(null);
    setLoadedAcceptedFigureSpec(null);
    setLoadedAcceptedDataUrl("");
    setShowLoadedAcceptedPreview(false);
    setLabelEditor(null);
    setAccepted(false);
    setError("");
    setStatusText("");
    if (typeof onReject === "function") {
      Promise.resolve(onReject({ action: "clear" })).catch(() => {});
    }
  }

  async function handleAccept() {
    if (!result || typeof onAccept !== "function") return;
    setLabelEditor(null);
    setLoading(true);
    setError("");
    try {
      let effectiveGuidedCircles = guidedCircles;
      let effectiveCircleOverrides = circleOverrides;
      let effectiveSelectedCircleSuggestionId = selectedCircleSuggestionId;
      let effectiveResult = result;

      if (activeCircleSuggestion && !hasActiveSuggestedCircleInPreview) {
        const circleId =
          String(activeCircleSuggestion.id || "").trim() || `guided_circle_${guidedCircles.length + 1}`;
        effectiveGuidedCircles = [
          ...(Array.isArray(guidedCircles) ? guidedCircles : []).filter((circle) => circle?.id !== circleId),
          {
            id: circleId,
            label: String(activeCircleSuggestion.label || "").trim() || null,
            center: {
              x: Number(activeCircleSuggestion.center?.x || 0),
              y: Number(activeCircleSuggestion.center?.y || 0)
            },
            radius: Number(activeCircleSuggestion.radius || 0)
          }
        ];
        effectiveCircleOverrides = {
          ...(circleOverrides || {}),
          [circleId]: {
            center: {
              x: Number(activeCircleSuggestion.center?.x || 0),
              y: Number(activeCircleSuggestion.center?.y || 0)
            },
            radius: Number(activeCircleSuggestion.radius || 0)
          }
        };
        effectiveSelectedCircleSuggestionId = null;
        effectiveResult = buildGeometryFigureResult(lines, stageSize, labelOverrides, {
          ...figureBuildOptions,
          guidedCircles: effectiveGuidedCircles,
          circleOverrides: effectiveCircleOverrides
        });
        setGuidedCircles(effectiveGuidedCircles);
        setCircleOverrides(effectiveCircleOverrides);
        setDraggableFreshCircleIds([circleId]);
        setSelectedCircleSuggestionId(null);
      }

      const adjustedAnnotationsById = new Map(
        canvasPreviewAnnotations.map((annotation) => [
          annotation.id,
          {
            pos: toFigureSpecPos({ x: annotation.x, y: annotation.y }, stageSize),
            anchorPos: toFigureSpecPos(
              {
                x: Number(annotation.anchorX ?? annotation.x ?? 0),
                y: Number(annotation.anchorY ?? annotation.y ?? 0)
              },
              stageSize
            )
          }
        ])
      );
      const adjustedFigureSpec =
        effectiveResult.figureSpec &&
        effectiveResult.figureSpec.entities &&
        Array.isArray(effectiveResult.figureSpec.entities.annotations)
          ? {
              ...effectiveResult.figureSpec,
              entities: {
                ...effectiveResult.figureSpec.entities,
                annotations: effectiveResult.figureSpec.entities.annotations.map((annotation) => {
                  const adjusted = adjustedAnnotationsById.get(annotation.id);
                  return adjusted
                    ? {
                        ...annotation,
                        pos: adjusted.pos,
                        anchorPos: adjusted.anchorPos
                      }
                    : annotation;
                })
              }
            }
          : result.figureSpec;
      let figureImageDataUrl = null;
      try {
        figureImageDataUrl = stageRef.current?.toDataURL?.({ pixelRatio: 2 }) || null;
      } catch (captureError) {
        figureImageDataUrl = null;
      }
      const acceptedResult = await onAccept({
        ...effectiveResult,
        figureSpec: adjustedFigureSpec,
        figureRenderSpec: adjustedFigureSpec,
        dataUrl: figureImageDataUrl,
        sourceLines: lines,
        sourceMeta: stageSize,
        editorState: createEditorStateSnapshot({
          lines,
          labelOverrides,
          pointLabelKinds,
          hiddenPointLabels,
          segmentStyleOverrides,
          segmentLabelOverrides,
          angleLabelOverrides,
          arcLabelOverrides,
          circleOverrides: effectiveCircleOverrides,
          guidedCircles: effectiveGuidedCircles,
          arcOverrides,
          familyOverrides,
          selectedCircleSuggestionId: effectiveSelectedCircleSuggestionId,
          stageSize,
          acceptedFigureSpec: adjustedFigureSpec,
          acceptedDataUrl: figureImageDataUrl
        }),
        status: "accepted"
      });
      if (acceptedResult) {
        setStatusText("Figure capture accepted.");
      }
      setLoadedAcceptedFigureSpec(adjustedFigureSpec || null);
      setLoadedAcceptedDataUrl(String(figureImageDataUrl || "").trim());
      setShowLoadedAcceptedPreview(false);
      setAcceptedSnapshot(
        createEditorStateSnapshot({
          lines,
          labelOverrides,
          pointLabelKinds,
          hiddenPointLabels,
          segmentStyleOverrides,
          segmentLabelOverrides,
          angleLabelOverrides,
          arcLabelOverrides,
          circleOverrides: effectiveCircleOverrides,
          guidedCircles: effectiveGuidedCircles,
          arcOverrides,
          familyOverrides,
          selectedCircleSuggestionId: effectiveSelectedCircleSuggestionId,
          stageSize,
          acceptedFigureSpec: adjustedFigureSpec,
          acceptedDataUrl: figureImageDataUrl
        })
      );
      setAccepted(true);
    } catch (err) {
      setError(err.message || "Failed to accept figure capture.");
    } finally {
      setLoading(false);
    }
  }

  async function handleReject() {
    if (showLoadedAcceptedPreview) {
      setShowLoadedAcceptedPreview(false);
      setStatusText("Switched to editable view.");
      setError("");
      return;
    }
    if (acceptedSnapshot) {
      suspendFreshCircleTrackingRef.current = true;
      setLines(acceptedSnapshot.lines || []);
      setRedoStack([]);
      setLabelOverrides(acceptedSnapshot.labelOverrides || {});
      setPointLabelKinds(acceptedSnapshot.pointLabelKinds || {});
      setHiddenPointLabels(acceptedSnapshot.hiddenPointLabels || {});
      setSegmentStyleOverrides(acceptedSnapshot.segmentStyleOverrides || {});
      setSegmentLabelOverrides(acceptedSnapshot.segmentLabelOverrides || {});
      setAngleLabelOverrides(acceptedSnapshot.angleLabelOverrides || {});
      setArcLabelOverrides(acceptedSnapshot.arcLabelOverrides || {});
      setCircleOverrides(acceptedSnapshot.circleOverrides || {});
      setGuidedCircles(acceptedSnapshot.guidedCircles || []);
      setDraggableFreshCircleIds([]);
      setArcOverrides(acceptedSnapshot.arcOverrides || {});
      setFamilyOverrides(acceptedSnapshot.familyOverrides || {});
      setSelectedCircleSuggestionId(acceptedSnapshot.selectedCircleSuggestionId || null);
      setLabelOverlayMode("none");
      setPanelTab("drawing");
      setAccepted(true);
      setLoadedAcceptedFigureSpec(acceptedSnapshot.acceptedFigureSpec || loadedAcceptedFigureSpec || null);
      setLoadedAcceptedDataUrl(String(acceptedSnapshot.acceptedDataUrl || loadedAcceptedDataUrl || "").trim());
      setError("");
      setStatusText("Reverted to the last accepted figure.");
      return;
    }

    suspendFreshCircleTrackingRef.current = true;
    setLines([]);
    setRedoStack([]);
    setLabelOverrides({});
    setPointLabelKinds({});
    setHiddenPointLabels({});
    setSegmentStyleOverrides({});
    setSegmentLabelOverrides({});
    setAngleLabelOverrides({});
    setArcLabelOverrides({});
    setCircleOverrides({});
    setGuidedCircles([]);
    setDraggableFreshCircleIds([]);
    setArcOverrides({});
    setFamilyOverrides({});
    setSelectedCircleSuggestionId(null);
    setLabelOverlayMode("none");
    setPanelTab("drawing");
    setLabelEditor(null);
    setAccepted(false);
    setLoadedAcceptedFigureSpec(null);
    setLoadedAcceptedDataUrl("");
    setShowLoadedAcceptedPreview(false);
    setStatusText("");
    setError("");
  }

  return (
    <div className="figure-assist-panel">
      <div className="question-meta">{title}</div>
      {helperText && <div className="question-meta">{helperText}</div>}
      <div className="work-tabs figure-assist-tabs">
        <button
          type="button"
          className={`tool-chip ${panelTab === "drawing" ? "active" : ""}`}
          onClick={() => setPanelTab("drawing")}
        >
          Drawing Slate
        </button>
        <button
          type="button"
          className={`tool-chip ${panelTab === "editor" ? "active" : ""}`}
          onClick={() => setPanelTab("editor")}
        >
          Editor
        </button>
      </div>
      <div className="figure-assist-tab-panel">
        {panelTab === "drawing" && (
        <div className="figure-assist-draw">
          <div className="figure-assist-toolbar-row">
            {enableValueLabels ? (
              <div className="figure-assist-label-toolbar">
                <button
                  type="button"
                  className={`tool-chip ${labelOverlayMode === "segment" ? "active" : ""}`}
                  onClick={() => toggleLabelOverlayMode("segment")}
                >
                  Side Labels
                </button>
                <button
                  type="button"
                  className={`tool-chip ${labelOverlayMode === "angle" ? "active" : ""}`}
                  onClick={() => toggleLabelOverlayMode("angle")}
                >
                  Angle Labels
                </button>
                <button
                  type="button"
                  className={`tool-chip ${labelOverlayMode === "arc" ? "active" : ""}`}
                  onClick={() => toggleLabelOverlayMode("arc")}
                >
                  Arc Labels
                </button>
                {labelOverlayMode !== "none" && (
                  <button type="button" className="tool-chip" onClick={() => setLabelOverlayMode("none")}>
                    Hide Targets
                  </button>
                )}
              </div>
            ) : (
              <div />
            )}
            <div className="figure-assist-stage-toolbar">
              <div className="slate-pen-menu" ref={strokeMenuRef}>
                <button
                  type="button"
                  className={`slate-icon-button ${showStrokeMenu ? "active" : ""}`}
                  title="Drawing style"
                  onClick={() => setShowStrokeMenu((prev) => !prev)}
                >
                  <svg viewBox="0 0 24 24" className="slate-icon-svg" aria-hidden="true">
                    <path
                      d="M4 17.2V20h2.8L17.6 9.2l-2.8-2.8L4 17.2z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13.8 7.8l2.8 2.8M4 20h4.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="slate-icon-fallback" aria-hidden="true">
                    {"\u270e"}
                  </span>
                </button>
                {showStrokeMenu && (
                  <div className="slate-pen-dropdown figure-assist-pen-dropdown">
                    <div className="figure-assist-pen-dropdown-section">
                      <div className="question-meta">Color</div>
                      <div className="slate-pen-swatches" role="listbox" aria-label="Figure line colors">
                        {[
                          { color: "#1d4ed8", label: "Blue" },
                          { color: "#0f172a", label: "Black" }
                        ].map((penOption) => (
                          <button
                            key={penOption.color}
                            type="button"
                            role="option"
                            aria-label={penOption.label}
                            aria-selected={drawStrokeColor === penOption.color}
                            className={`slate-pen-swatch ${drawStrokeColor === penOption.color ? "active" : ""}`}
                            onClick={() => {
                              setDrawStrokeColor(penOption.color);
                              setShowStrokeMenu(false);
                            }}
                          >
                            <span className="slate-pen-swatch-fill" style={{ backgroundColor: penOption.color }} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="figure-assist-pen-dropdown-section">
                      <div className="question-meta">Line</div>
                      <div className="figure-assist-stroke-style-options">
                        {[
                          { value: "solid", label: "Solid" },
                          { value: "dashed", label: "Dashed" }
                        ].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`tool-chip ${drawStrokeStyle === option.value ? "active" : ""}`}
                            onClick={() => {
                              setDrawStrokeStyle(option.value);
                              setShowStrokeMenu(false);
                            }}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="figure-assist-icon-toolbar" aria-label="Figure drawing actions">
                <button
                  type="button"
                  className="slate-icon-button"
                  title="Undo last stroke"
                  onClick={handleUndo}
                  disabled={!canUndo}
                >
                  <svg viewBox="0 0 24 24" className="slate-icon-svg" aria-hidden="true">
                    <path
                      d="M9 7l-5 5 5 5M5 12h8a6 6 0 110 12h-2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="slate-icon-fallback" aria-hidden="true">
                    {"\u21b6"}
                  </span>
                </button>
                <button
                  type="button"
                  className="slate-icon-button"
                  title="Redo stroke"
                  onClick={handleRedo}
                  disabled={!canRedo}
                >
                  <svg viewBox="0 0 24 24" className="slate-icon-svg" aria-hidden="true">
                    <path
                      d="M15 7l5 5-5 5M19 12h-8a6 6 0 100 12h2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="slate-icon-fallback" aria-hidden="true">
                    {"\u21b7"}
                  </span>
                </button>
                <button
                  type="button"
                  className="slate-icon-button"
                  title="Clear figure"
                  onClick={() => setShowClearConfirm(true)}
                  disabled={!canClear}
                >
                  <svg viewBox="0 0 24 24" className="slate-icon-svg" aria-hidden="true">
                    <path
                      d="M4 7h16M9 7V4h6v3M7 7l1 12h8l1-12M10 11v6M14 11v6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="slate-icon-fallback" aria-hidden="true">
                    {"\u232b"}
                  </span>
                </button>
              </div>
              <div className="figure-assist-zoom-toolbar">
                <button
                  type="button"
                  className="tool-chip"
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= MIN_ZOOM_LEVEL}
                >
                  -
                </button>
                <button type="button" className="tool-chip" onClick={handleZoomReset}>
                  {Math.round(zoomLevel * 100)}%
                </button>
                <button
                  type="button"
                  className="tool-chip"
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= MAX_ZOOM_LEVEL}
                >
                  +
                </button>
              </div>
            </div>
          </div>
          <div className="figure-assist-stage" ref={containerRef}>
            <div
              ref={stageViewportRef}
              className="figure-assist-stage-viewport"
              style={{ width: `${stageSize.width}px`, height: `${stageSize.height}px` }}
            >
              {showLoadedAcceptedPreview && (loadedAcceptedDataUrl || loadedAcceptedFigureSpec) ? (
                <div
                  className="figure-assist-stage-content figure-assist-stage-preview"
                  style={{ width: `${stageSize.width}px`, height: `${stageSize.height}px` }}
                >
                  {loadedAcceptedDataUrl ? (
                    <img
                      className="attempt-figure-image"
                      src={loadedAcceptedDataUrl}
                      alt="Saved accepted figure"
                    />
                  ) : loadedAcceptedFigureSpec ? (
                    <FigureRender
                      figureSpec={loadedAcceptedFigureSpec}
                      cropToContent
                      contentPadding={8}
                      theme="accepted"
                    />
                  ) : null}
                </div>
              ) : (
              <div
                className="figure-assist-stage-content"
                style={{ width: `${zoomedStageSize.width}px`, height: `${zoomedStageSize.height}px` }}
              >
                <Stage
                  ref={stageRef}
                  width={zoomedStageSize.width}
                  height={zoomedStageSize.height}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                >
                  <Layer scaleX={zoomLevel} scaleY={zoomLevel}>
                {activeRawLine ? (
                  <Line
                    key="raw-active-line"
                    points={activeRawLine.points}
                    stroke={activeRawLine.color}
                    strokeWidth={RAW_STROKE_WIDTH}
                    dash={resolveSegmentDashPattern(activeRawLine.strokeStyle)}
                    tension={0.45}
                    lineCap="round"
                    lineJoin="round"
                    opacity={0.4}
                  />
                ) : null}
                {previewSegments.map((segment) => (
                  <Line
                    key={segment.id}
                    points={[segment.from.x, segment.from.y, segment.to.x, segment.to.y]}
                    stroke={accepted ? "#1d4ed8" : String(segment.color || "#0f172a")}
                    strokeWidth={PREVIEW_STROKE_WIDTH}
                    dash={Array.isArray(segment.dash) ? segment.dash : resolveSegmentDashPattern(segment.strokeStyle)}
                    lineCap="round"
                    lineJoin="round"
                    name={
                      NON_SOLID_SEGMENT_STYLES.includes(normalizeSegmentStrokeStyle(segment.strokeStyle, "solid"))
                        ? "figure-interactive"
                        : undefined
                    }
                    onPointerDown={(event) => handleSegmentStyleInteraction(event, segment)}
                    onTap={(event) => handleSegmentStyleInteraction(event, segment)}
                  />
                ))}
                {activeCircleSuggestion && !hasActiveSuggestedCircleInPreview && (
                    <Circle
                      x={activeCircleSuggestion.center.x}
                      y={activeCircleSuggestion.center.y}
                      radius={activeCircleSuggestion.radius}
                      stroke="#38bdf8"
                      strokeWidth={PREVIEW_GUIDE_STROKE_WIDTH}
                      dash={[7, 5]}
                      opacity={0.72}
                      fillEnabled={false}
                      listening={false}
                    />
                  )}
                {previewCircles.map((circle) => (
                  <Circle
                    key={circle.id}
                    x={circle.center.x}
                    y={circle.center.y}
                    radius={circle.radius}
                    stroke={accepted ? "#1d4ed8" : "#0f172a"}
                    strokeWidth={PREVIEW_CIRCLE_STROKE_WIDTH}
                    fillEnabled={false}
                    listening={false}
                  />
                ))}
                {previewArcs.map((arc) => (
                  <Line
                    key={arc.id}
                    points={(Array.isArray(arc.points) ? arc.points : []).flatMap((point) => [point.x, point.y])}
                    stroke={accepted ? "#1d4ed8" : "#0f172a"}
                    strokeWidth={PREVIEW_CIRCLE_STROKE_WIDTH}
                    lineCap="round"
                    lineJoin="round"
                    listening={false}
                  />
                ))}
                {previewRightAngleMarkers.map((marker) => (
                  <Line
                    key={marker.id}
                    points={marker.points.flatMap((point) => [point.x, point.y])}
                    stroke={accepted ? "#1d4ed8" : "#0f172a"}
                    strokeWidth={PREVIEW_MARKER_STROKE_WIDTH}
                    lineCap="round"
                    lineJoin="round"
                    listening={false}
                  />
                ))}
                {previewLengthTickMarkers.map((marker) => (
                  <Line
                    key={marker.id}
                    points={marker.points.flatMap((point) => [point.x, point.y])}
                    stroke={accepted ? "#1d4ed8" : "#0f172a"}
                    strokeWidth={PREVIEW_MARKER_STROKE_WIDTH}
                    hitStrokeWidth={14}
                    lineCap="round"
                    lineJoin="round"
                    onPointerDown={(event) =>
                      handleMarkerFamilyInteraction(
                        event,
                        marker.id,
                        marker.familyId,
                        marker.tickCount,
                        "tick"
                      )
                    }
                    onTap={(event) =>
                      handleMarkerFamilyInteraction(
                        event,
                        marker.id,
                        marker.familyId,
                        marker.tickCount,
                        "tick"
                      )
                    }
                  />
                ))}
                {previewParallelArrowMarkers.map((marker) => (
                  <Line
                    key={marker.id}
                    points={marker.points.flatMap((point) => [point.x, point.y])}
                    stroke={accepted ? "#1d4ed8" : "#0f172a"}
                    strokeWidth={PREVIEW_MARKER_STROKE_WIDTH}
                    hitStrokeWidth={14}
                    lineCap="round"
                    lineJoin="round"
                    onPointerDown={(event) =>
                      handleMarkerFamilyInteraction(
                        event,
                        marker.id,
                        marker.familyId,
                        marker.arrowCount,
                        "arrow"
                      )
                    }
                    onTap={(event) =>
                      handleMarkerFamilyInteraction(
                        event,
                        marker.id,
                        marker.familyId,
                        marker.arrowCount,
                        "arrow"
                      )
                    }
                  />
                ))}
                {previewAngleArcMarkers.map((marker) => (
                  <Line
                    key={marker.id}
                    points={marker.points.flatMap((point) => [point.x, point.y])}
                    stroke={accepted ? "#1d4ed8" : "#0f172a"}
                    strokeWidth={PREVIEW_MARKER_STROKE_WIDTH}
                    hitStrokeWidth={14}
                    lineCap="round"
                    lineJoin="round"
                    onPointerDown={(event) =>
                      handleMarkerFamilyInteraction(
                        event,
                        marker.id,
                        marker.familyId,
                        marker.arcCount,
                        "arc"
                      )
                    }
                    onTap={(event) =>
                      handleMarkerFamilyInteraction(
                        event,
                        marker.id,
                        marker.familyId,
                        marker.arcCount,
                        "arc"
                      )
                    }
                  />
                ))}
                {previewPoints.map((point) => {
                  const centerCircle = circleByCenterNodeId.get(point.id);
                  const isCircleCenter = Boolean(centerCircle);
                  const canDragCircleCenter =
                    Boolean(centerCircle) && draggableFreshCircleIds.includes(centerCircle.id);
                  return (
                    <Circle
                      key={`point-${point.id}`}
                      x={point.x}
                      y={point.y}
                      radius={isCircleCenter ? PREVIEW_CENTER_POINT_RADIUS : PREVIEW_POINT_RADIUS}
                      fill={isCircleCenter ? "#ffffff" : "#0f172a"}
                      stroke={isCircleCenter ? (accepted ? "#1d4ed8" : "#0f172a") : undefined}
                      strokeWidth={isCircleCenter ? PREVIEW_CENTER_POINT_STROKE : 0}
                      draggable={canDragCircleCenter}
                      onPointerDown={(event) => {
                        if (canDragCircleCenter) {
                          event.cancelBubble = true;
                        }
                      }}
                      onDragMove={(event) => {
                        if (canDragCircleCenter && centerCircle) handleCircleCenterDrag(centerCircle, event);
                      }}
                      onDragEnd={(event) => {
                        if (canDragCircleCenter && centerCircle) handleCircleCenterDrag(centerCircle, event);
                      }}
                      onMouseDown={(event) => {
                        if (isCircleCenter) {
                          if (canDragCircleCenter) {
                            event.cancelBubble = true;
                          }
                          return;
                        }
                        event.cancelBubble = true;
                        openPointLabelEditor(point);
                      }}
                      onClick={(event) => {
                        if (isCircleCenter) return;
                        event.cancelBubble = true;
                        openPointLabelEditor(point);
                      }}
                      onTap={(event) => {
                        if (isCircleCenter) return;
                        event.cancelBubble = true;
                        openPointLabelEditor(point);
                      }}
                    />
                  );
                })}
                {previewCircles.map((circle) => (
                  <Circle
                    key={`${circle.id}-radius-handle`}
                    x={circle.center.x + circle.radius}
                    y={circle.center.y}
                    radius={5.4}
                    fill="#ffffff"
                    stroke="#38bdf8"
                    strokeWidth={2}
                    draggable
                    onPointerDown={(event) => {
                      event.cancelBubble = true;
                    }}
                    onDragMove={(event) => handleCircleRadiusDrag(circle, event)}
                    onDragEnd={(event) => handleCircleRadiusDrag(circle, event)}
                  />
                ))}
                {previewArcs.map((arc) => (
                  <Circle
                    key={`${arc.id}-center-handle`}
                    x={arc.center.x}
                    y={arc.center.y}
                    radius={5}
                    fill="#ffffff"
                    stroke="#38bdf8"
                    strokeWidth={2}
                    draggable
                    onPointerDown={(event) => {
                      event.cancelBubble = true;
                    }}
                    onDragMove={(event) => handleArcCenterDrag(arc, event)}
                    onDragEnd={(event) => handleArcCenterDrag(arc, event)}
                  />
                ))}
                {previewPoints
                  .filter((point) => String(point?.label || "").trim())
                  .map((point) => (
                    <Group key={`label-${point.id}`}>
                      {(() => {
                        const rect = labelLayouts[point.id];
                        const leaderPoints = computeLeaderPoints(point, rect, 8);
                        return leaderPoints ? (
                          <Line
                            points={leaderPoints}
                            stroke="#94a3b8"
                            strokeWidth={1.2}
                            dash={[5, 4]}
                            listening={false}
                          />
                        ) : null;
                      })()}
                      <Text
                        name="figure-interactive"
                        x={labelLayouts[point.id]?.x ?? point.x + 6}
                        y={labelLayouts[point.id]?.y ?? point.y - 18}
                        text={point.label}
                        fontSize={PREVIEW_LABEL_FONT_SIZE}
                        fill="#0f172a"
                        fontStyle="600"
                        onMouseDown={(event) => {
                          event.cancelBubble = true;
                          openPointLabelEditor(point);
                        }}
                        onClick={(event) => {
                          event.cancelBubble = true;
                          openPointLabelEditor(point);
                        }}
                        onPointerDown={(event) => {
                          event.cancelBubble = true;
                          openPointLabelEditor(point);
                        }}
                        onTap={(event) => {
                          event.cancelBubble = true;
                          openPointLabelEditor(point);
                        }}
                      />
                    </Group>
                  ))}
                {enableValueLabels &&
                  visibleSegmentLabelCandidates.map((candidate) => {
                    const chip = buildCanvasLabelChip(candidate, "segment");
                    const leaderPoints = computeLeaderPoints(
                      { x: Number(candidate.anchorX || candidate.x || 0), y: Number(candidate.anchorY || candidate.y || 0) },
                      chip
                    );
                    return (
                      <Group
                        key={`segment-label-handle-${candidate.id}`}
                        name="figure-interactive"
                        onMouseDown={(event) => {
                          event.cancelBubble = true;
                          openEntityLabelEditor("segment", candidate);
                        }}
                        onClick={(event) => {
                          event.cancelBubble = true;
                          openEntityLabelEditor("segment", candidate);
                        }}
                        onPointerDown={(event) => {
                          event.cancelBubble = true;
                          openEntityLabelEditor("segment", candidate);
                        }}
                        onTap={(event) => {
                          event.cancelBubble = true;
                          openEntityLabelEditor("segment", candidate);
                        }}
                      >
                        {leaderPoints && (
                          <Line
                            points={leaderPoints}
                            stroke="#94a3b8"
                            strokeWidth={1}
                            dash={[3, 3]}
                            listening={false}
                          />
                        )}
                        <Rect
                          x={chip.x}
                          y={chip.y}
                          width={chip.width}
                          height={chip.height}
                          cornerRadius={11}
                          fill={candidate.text ? "#eff6ff" : "#ffffff"}
                          stroke={candidate.kind === "variable" ? "#2563eb" : "#94a3b8"}
                          strokeWidth={1.25}
                          dash={candidate.text ? undefined : [3, 2]}
                        />
                        <Text
                          x={chip.x + 7}
                          y={chip.y + 5}
                          text={chip.text}
                          fontSize={11.5}
                          fill={candidate.kind === "variable" ? "#1d4ed8" : "#475569"}
                          listening={false}
                        />
                      </Group>
                    );
                  })}
                {enableValueLabels &&
                  visibleAngleLabelCandidates.map((candidate) => {
                    const chip = buildCanvasLabelChip(candidate, "angle");
                    const leaderPoints = computeLeaderPoints(
                      { x: Number(candidate.anchorX || candidate.x || 0), y: Number(candidate.anchorY || candidate.y || 0) },
                      chip
                    );
                    return (
                      <Group
                        key={`angle-label-handle-${candidate.id}`}
                        name="figure-interactive"
                        onMouseDown={(event) => {
                          event.cancelBubble = true;
                          openEntityLabelEditor("angle", candidate);
                        }}
                        onClick={(event) => {
                          event.cancelBubble = true;
                          openEntityLabelEditor("angle", candidate);
                        }}
                        onPointerDown={(event) => {
                          event.cancelBubble = true;
                          openEntityLabelEditor("angle", candidate);
                        }}
                        onTap={(event) => {
                          event.cancelBubble = true;
                          openEntityLabelEditor("angle", candidate);
                        }}
                      >
                        {leaderPoints && (
                          <Line
                            points={leaderPoints}
                            stroke="#94a3b8"
                            strokeWidth={1}
                            dash={[3, 3]}
                            listening={false}
                          />
                        )}
                        <Rect
                          x={chip.x}
                          y={chip.y}
                          width={chip.width}
                          height={chip.height}
                          cornerRadius={11}
                          fill={candidate.text ? "#eff6ff" : "#ffffff"}
                          stroke={candidate.kind === "variable" ? "#2563eb" : "#94a3b8"}
                          strokeWidth={1.25}
                          dash={candidate.text ? undefined : [3, 2]}
                        />
                        <Text
                          x={chip.x + 7}
                          y={chip.y + 5}
                          text={chip.text}
                          fontSize={11.5}
                          fill={candidate.kind === "variable" ? "#1d4ed8" : "#475569"}
                          listening={false}
                        />
                      </Group>
                    );
                  })}
                {enableValueLabels &&
                  visibleArcLabelCandidates.map((candidate) => {
                    const chip = buildCanvasLabelChip(candidate, "angle");
                    const leaderPoints = computeLeaderPoints(
                      { x: Number(candidate.anchorX || candidate.x || 0), y: Number(candidate.anchorY || candidate.y || 0) },
                      chip
                    );
                    return (
                      <Group
                        key={`arc-label-handle-${candidate.id}`}
                        name="figure-interactive"
                        onMouseDown={(event) => {
                          event.cancelBubble = true;
                          openEntityLabelEditor("arc", candidate);
                        }}
                        onClick={(event) => {
                          event.cancelBubble = true;
                          openEntityLabelEditor("arc", candidate);
                        }}
                        onPointerDown={(event) => {
                          event.cancelBubble = true;
                          openEntityLabelEditor("arc", candidate);
                        }}
                        onTap={(event) => {
                          event.cancelBubble = true;
                          openEntityLabelEditor("arc", candidate);
                        }}
                      >
                        {leaderPoints && (
                          <Line
                            points={leaderPoints}
                            stroke="#94a3b8"
                            strokeWidth={1}
                            dash={[3, 3]}
                            listening={false}
                          />
                        )}
                        <Rect
                          x={chip.x}
                          y={chip.y}
                          width={chip.width}
                          height={chip.height}
                          cornerRadius={11}
                          fill={candidate.text ? "#eff6ff" : "#ffffff"}
                          stroke={candidate.kind === "variable" ? "#2563eb" : "#94a3b8"}
                          strokeWidth={1.25}
                          dash={candidate.text ? undefined : [3, 2]}
                        />
                        <Text
                          x={chip.x + 7}
                          y={chip.y + 5}
                          text={chip.text}
                          fontSize={11.5}
                          fill={candidate.kind === "variable" ? "#1d4ed8" : "#475569"}
                          listening={false}
                        />
                      </Group>
                    );
                  })}
                {canvasPreviewAnnotations.map((annotation) => (
                  (() => {
                    const text = String(annotation.text || "");
                    const rect = {
                      x: Number(annotation.x || 0),
                      y: Number(annotation.y || 0) - (PREVIEW_LABEL_FONT_SIZE - 1),
                      width: Math.max(16, text.length * 7.1 + 6),
                      height: PREVIEW_LABEL_FONT_SIZE + 4
                    };
                    const leaderPoints = computeLeaderPoints(
                      { x: Number(annotation.anchorX ?? annotation.x ?? 0), y: Number(annotation.anchorY ?? annotation.y ?? 0) },
                      rect
                    );
                    return (
                      <Group
                        key={`annotation-${annotation.id}`}
                        name={enableValueLabels && annotation.targetType && annotation.targetId ? "figure-interactive" : undefined}
                        onMouseDown={(event) => {
                          if (!enableValueLabels || !annotation.targetType || !annotation.targetId) return;
                          event.cancelBubble = true;
                          openEntityLabelEditor(annotation.targetType, {
                            id: annotation.targetId,
                            text: annotation.text,
                            kind: annotation.kind,
                            x: annotation.x,
                            y: annotation.y
                          });
                        }}
                        onClick={(event) => {
                          if (!enableValueLabels || !annotation.targetType || !annotation.targetId) return;
                          event.cancelBubble = true;
                          openEntityLabelEditor(annotation.targetType, {
                            id: annotation.targetId,
                            text: annotation.text,
                            kind: annotation.kind,
                            x: annotation.x,
                            y: annotation.y
                          });
                        }}
                      >
                        {leaderPoints && (
                          <Line
                            points={leaderPoints}
                            stroke="#94a3b8"
                            strokeWidth={1}
                            dash={[3, 3]}
                            listening={false}
                          />
                        )}
                        <Text
                          x={annotation.x}
                          y={annotation.y}
                          text={annotation.text}
                          fontSize={PREVIEW_LABEL_FONT_SIZE - 1}
                          fill={accepted ? "#1d4ed8" : "#0f172a"}
                          onPointerDown={(event) => {
                            if (!enableValueLabels || !annotation.targetType || !annotation.targetId) return;
                            event.cancelBubble = true;
                            openEntityLabelEditor(annotation.targetType, {
                              id: annotation.targetId,
                              text: annotation.text,
                              kind: annotation.kind,
                              x: annotation.x,
                              y: annotation.y
                            });
                          }}
                          onTap={(event) => {
                            if (!enableValueLabels || !annotation.targetType || !annotation.targetId) return;
                            event.cancelBubble = true;
                            openEntityLabelEditor(annotation.targetType, {
                              id: annotation.targetId,
                              text: annotation.text,
                              kind: annotation.kind,
                              x: annotation.x,
                              y: annotation.y
                            });
                          }}
                        />
                      </Group>
                    );
                  })()
                ))}
                  </Layer>
                </Stage>
              </div>
              )}
                {!showLoadedAcceptedPreview && labelEditor && (
                  <div
                    className="figure-assist-inline-editor"
                    style={{
                      left: `${labelEditor.left}px`,
                      top: `${labelEditor.top}px`
                    }}
                  >
                <input
                  ref={labelEditorInputRef}
                  value={labelEditor.value}
                  maxLength={labelEditor.targetKind === "point" ? 8 : 16}
                  onChange={(event) =>
                    setLabelEditor((prev) =>
                      prev
                        ? {
                            ...prev,
                            value: event.target.value
                          }
                        : prev
                    )
                  }
                  onBlur={commitLabelEditor}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      commitLabelEditor();
                    }
                    if (event.key === "Escape") {
                      event.preventDefault();
                      setLabelEditor(null);
                    }
                  }}
                />
                {enableValueLabels && (
                  <div className="figure-assist-inline-toggle">
                    <button
                      type="button"
                      className={`tool-chip ${labelEditor.kind !== "variable" ? "active" : ""}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() =>
                        setLabelEditor((prev) => (prev ? { ...prev, kind: "constant" } : prev))
                      }
                    >
                      Const
                    </button>
                    <button
                      type="button"
                      className={`tool-chip ${labelEditor.kind === "variable" ? "active" : ""}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() =>
                        setLabelEditor((prev) => (prev ? { ...prev, kind: "variable" } : prev))
                      }
                    >
                      Var
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className="tool-chip"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={clearLabelEditor}
                >
                  Clear
                </button>
                  </div>
                )}
            </div>
            <div
              className="figure-assist-resize-handle"
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize drawing area"
              onPointerDown={handleResizePointerDown}
              onPointerMove={handleResizePointerMove}
              onPointerUp={handleResizePointerEnd}
              onPointerCancel={handleResizePointerEnd}
              onLostPointerCapture={handleResizePointerEnd}
            >
              <span className="figure-assist-resize-grip" />
            </div>
          </div>
          {showAcceptRejectControls && (
            <div className="figure-assist-actions">
              <button
                className="primary-button"
                type="button"
                onClick={handleAccept}
                disabled={loading || (!previewSegments.length && !previewCircles.length && !previewArcs.length) || !onAccept}
              >
                {accepted ? "Accepted" : "Accept"}
              </button>
              <button
                className="secondary-button"
                type="button"
                onClick={handleReject}
                disabled={loading || (!acceptedSnapshot && !lines.length)}
              >
                {showLoadedAcceptedPreview ? "Edit" : acceptedSnapshot ? "Revert" : "Reject"}
              </button>
            </div>
          )}
          {previewCircleSuggestions.length > 0 && (
            <div className="figure-assist-actions figure-assist-secondary-actions">
              {previewCircleSuggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  className={`secondary-button ${selectedCircleSuggestionId === suggestion.id ? "active" : ""}`}
                  type="button"
                  onClick={() =>
                    setSelectedCircleSuggestionId((prev) => (prev === suggestion.id ? null : suggestion.id))
                  }
                >
                  {selectedCircleSuggestionId === suggestion.id ? `Hide ${suggestion.label}` : `Guide ${suggestion.label}`}
                </button>
              ))}
              {activeCircleSuggestion && (
                <button className="secondary-button" type="button" onClick={applyCircleSuggestion}>
                  Use {activeCircleSuggestion.label}
                </button>
              )}
            </div>
          )}
          {statusText && <div className="question-meta">{statusText}</div>}
          {error && <div className="error-text">{error}</div>}
        </div>
        )}
        {panelTab === "editor" && (
        <div className="figure-assist-editor">
          <div className="figure-assist-preview-card">
            <div className="analysis-label">Detected Geometry</div>
            <div className="analysis-text">
              Confidence: {Math.round(Number(result?.confidence?.overall || 0) * 100)}%
            </div>
              <div className="analysis-text">
              {previewSegments.length || previewCircles.length || previewArcs.length
                ? `${previewSegments.length} segment(s), ${previewPoints.length} point(s), ${previewCircles.length} circle(s), ${previewArcs.length} arc(s)`
                : "Draw a near-straight segment, open arc, or closed circle to place the first geometry primitive."}
            </div>
            {(previewAngleArcMarkers.length ||
              previewLengthTickMarkers.length ||
              previewParallelArrowMarkers.length) > 0 && (
              <div className="question-meta">
                Click a snapped arc, tick, or arrow marker to cycle it between 1, 2, and 3.
              </div>
            )}
            <div className="question-meta">
              Use the pen icon in the top-right to switch between black, blue, solid, and dashed drawing.
            </div>
            {hasStyledSegments && (
              <div className="question-meta">
                Click a dashed line to switch between dashed and dotted.
              </div>
            )}
            {activeCircleSuggestion && (
              <div className="question-meta">
                Use the selected {activeCircleSuggestion.label?.toLowerCase() || "guide"} to commit a real circle. After that, drag the white center handle or the blue radius handle to adjust it.
              </div>
            )}
            {enableValueLabels && canReviewLabels && (
              <div className="question-meta">
                Use Side Labels or Angle Labels in the Drawing Slate tab to choose only the targets you want to author. This editor tab is the fallback review surface.
              </div>
            )}
          </div>
          {canReviewLabels && (
            <div className="figure-assist-summary-card">
              <details className="figure-assist-fallback-editor">
                <summary>Review Labels (Fallback)</summary>
                {previewPoints.length > 0 && (
                  <>
                    <div className="analysis-label">Point Labels</div>
                    <div className="figure-label-grid">
                      {previewPoints.map((point) => (
                        <label
                          key={`label-input-${point.id}`}
                          className={`figure-label-row ${enableValueLabels ? "figure-label-row-wide" : ""}`}
                        >
                          <span className="question-meta">Point</span>
                          <input
                            value={labelOverrides[point.id] ?? unwrapTemplateToken(point.label)}
                            maxLength={8}
                            onChange={(event) => updatePointLabel(point.id, event.target.value)}
                          />
                          {enableValueLabels && (
                            <select
                              value={pointLabelKinds[point.id] || "constant"}
                              onChange={(event) => updatePointLabelKind(point.id, event.target.value)}
                            >
                              <option value="constant">Constant</option>
                              <option value="variable">Variable</option>
                            </select>
                          )}
                        </label>
                      ))}
                    </div>
                  </>
                )}
                {enableValueLabels && previewSegmentLabelCandidates.length > 0 && (
                  <>
                    <div className="analysis-label">Segment Labels</div>
                    <div className="figure-label-grid">
                      {previewSegmentLabelCandidates.map((candidate) => (
                        <label key={`segment-label-${candidate.id}`} className="figure-label-row figure-label-row-wide">
                          <span className="question-meta">{candidate.label}</span>
                          <input
                            value={candidate.text}
                            maxLength={16}
                            placeholder="e.g. x or 5 cm"
                            onChange={(event) =>
                              updateEntityLabelOverride("segment", candidate.id, "text", event.target.value)
                            }
                          />
                          <select
                            value={candidate.kind}
                            onChange={(event) =>
                              updateEntityLabelOverride("segment", candidate.id, "kind", event.target.value)
                            }
                          >
                            <option value="constant">Constant</option>
                            <option value="variable">Variable</option>
                          </select>
                        </label>
                      ))}
                    </div>
                  </>
                )}
                {enableValueLabels && previewAngleLabelCandidates.length > 0 && (
                  <>
                    <div className="analysis-label">Angle Labels</div>
                    <div className="figure-label-grid">
                {previewAngleLabelCandidates.map((candidate) => (
                  <label key={`angle-label-${candidate.id}`} className="figure-label-row figure-label-row-wide">
                    <span className="question-meta">{candidate.label}</span>
                    <input
                      value={candidate.text}
                      maxLength={16}
                      placeholder="e.g. x or 50°"
                      onChange={(event) =>
                        updateEntityLabelOverride("angle", candidate.id, "text", event.target.value)
                      }
                    />
                    <select
                      value={candidate.kind}
                      onChange={(event) =>
                        updateEntityLabelOverride("angle", candidate.id, "kind", event.target.value)
                      }
                    >
                      <option value="constant">Constant</option>
                      <option value="variable">Variable</option>
                    </select>
                  </label>
                ))}
              </div>
                  </>
                )}
                {enableValueLabels && previewArcLabelCandidates.length > 0 && (
                  <>
                    <div className="analysis-label">Arc Labels</div>
                    <div className="figure-label-grid">
                      {previewArcLabelCandidates.map((candidate) => (
                        <label key={`arc-label-${candidate.id}`} className="figure-label-row figure-label-row-wide">
                          <span className="question-meta">{candidate.label}</span>
                          <input
                            value={candidate.text}
                            maxLength={16}
                            placeholder="e.g. x or 60 deg"
                            onChange={(event) =>
                              updateEntityLabelOverride("arc", candidate.id, "text", event.target.value)
                            }
                          />
                          <select
                            value={candidate.kind}
                            onChange={(event) =>
                              updateEntityLabelOverride("arc", candidate.id, "kind", event.target.value)
                            }
                          >
                            <option value="constant">Constant</option>
                            <option value="variable">Variable</option>
                          </select>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </details>
            </div>
          )}
          <div className="figure-assist-summary-card">
            <div className="analysis-label">Entities</div>
            {entitySummary.length ? (
              <div className="figure-summary-list">
                {entitySummary.map((item, index) => (
                  <div key={`entity-${index}`} className="analysis-text">
                    {item}
                  </div>
                ))}
              </div>
            ) : (
              <div className="analysis-text">No entities captured yet.</div>
            )}
            <div className="analysis-label">Relations</div>
            {relationSummary.length ? (
              <div className="figure-summary-list">
                {relationSummary.map((item, index) => (
                  <div key={`relation-${index}`} className="analysis-text">
                    {item}
                  </div>
                ))}
              </div>
            ) : (
              <div className="analysis-text">No relations captured yet.</div>
            )}
            <div className="analysis-label">Dependencies</div>
            {dependencySummary.length ? (
              <div className="figure-summary-list">
                {dependencySummary.map((item, index) => (
                  <div key={`dependency-${index}`} className="analysis-text">
                    {item}
                  </div>
                ))}
              </div>
            ) : (
              <div className="analysis-text">No dependencies captured.</div>
            )}
            {givenSummary.length > 0 && (
              <>
                <div className="analysis-label">Givens</div>
                <div className="figure-summary-list">
                  {givenSummary.map((item, index) => (
                    <div key={`given-${index}`} className="analysis-text">
                      {item}
                    </div>
                  ))}
                </div>
              </>
            )}
            {warnings.length > 0 && (
              <>
                <div className="analysis-label">Warnings</div>
                <div className="figure-summary-list">
                  {warnings.map((item, index) => (
                    <div key={`warning-${index}`} className="analysis-text">
                      {item}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        )}
      </div>
      <ConfirmDialog
        open={showClearConfirm}
        title="Clear figure?"
        message="This will remove the current figure drawing and editor changes."
        confirmLabel="Clear"
        onCancel={() => setShowClearConfirm(false)}
        onConfirm={() => {
          setShowClearConfirm(false);
          handleClear();
        }}
      />
    </div>
  );
}

export default FigureAssistPanel;
